import {runInAction} from "mobx";
import {Vector3} from "three";
import {isDefaultField} from "../models/field/FieldUtils";
import type {Space} from "../models/Space";
import type {IFilterState} from "../models/filter/Filter";
import type {Xyicon} from "../models/Xyicon";
import type {BoundarySpaceMap} from "../models/BoundarySpaceMap";
import type {Markup} from "../models/Markup";
import type {Link} from "../models/Link";
import type {User, ExternalUser} from "../models/User";
import type {UserGroup} from "../models/UserGroup";
import type {PermissionSet} from "../models/permission/PermissionSet";
import type {PortfolioGroup} from "../models/PortfolioGroup";
import type {Boundary} from "../models/Boundary";
import type {IResponse} from "../TransportLayer";
import type {View} from "../models/View";
import {updateRecentlyUsedViews} from "../models/ViewUtils";
import type {ICaptionSettings, IFormattingRuleSet, IViewColumn, IViewSort} from "../models/ViewUtils";
import {MarkupType, XyiconFeature, Permission, FieldDataType, LinkType} from "../../generated/api/base";
import type {IEditableItemModel, IModel} from "../models/Model";
import type {Type} from "../models/Type";
import {FieldDataTypes} from "../models/field/FieldDataTypes";
import type {
	UpdateXyiconsModelRequest,
	DeletedUsersDto,
	DeletedUserInfoDto,
	SpaceDeleteDto,
	SpaceVersionsDeleteDto,
	MergeBoundaryRequest,
	MergedBoundaryDto,
	DeleteUsersRequest,
} from "../../generated/api/base";
import {FeatureService} from "../services/FeatureService";
import {FormatUtils} from "../../utils/FormatUtils";
import type {Portfolio} from "../models/Portfolio";
import type {Field as FieldModel, IFieldAdapter, IFieldPointer, Field} from "../models/field/Field";
import {StringUtils} from "../../utils/data/string/StringUtils";
import type {IFilterOperatorConfig} from "../models/filter/operator/FilterOperators";
import {FilterOperators} from "../models/filter/operator/FilterOperators";
import {WarningWindow} from "../../ui/modules/abstract/popups/WarningWindow";
import type {Catalog} from "../models/Catalog";
import {ObjectUtils} from "../../utils/data/ObjectUtils";
import {ReportSortDirection, type Report} from "../models/Report";
import {compareTableValues} from "../../ui/widgets/table/TableUtils";
import {selectWorkspaceViewTabForFeature} from "../../ui/5.0/topbar/ViewTabsCommon";
import type {BoundarySpaceMap3D} from "../../ui/modules/space/spaceeditor/logic3d/elements3d/BoundarySpaceMap3D";
import {XHRLoader} from "../../utils/loader/XHRLoader";
import {NotificationType} from "../../ui/notification/Notification";
import {notify} from "../../utils/Notify";
import type {IBooleanFieldSettingsDefinition} from "../../ui/modules/settings/modules/field/datatypes/BooleanFieldSettings";
import {MathUtils} from "../../utils/math/MathUtils";
import {ArrayUtils} from "../../utils/data/array/ArrayUtils";
import type {SpaceItem} from "../../ui/modules/space/spaceeditor/logic3d/elements3d/SpaceItem";
import type {Xyicon3D} from "../../ui/modules/space/spaceeditor/logic3d/elements3d/Xyicon3D";
import type {DistanceUnitName} from "../../ui/modules/space/spaceeditor/logic3d/Constants";
import {Constants} from "../../ui/modules/space/spaceeditor/logic3d/Constants";
import {THREEUtils} from "../../utils/THREEUtils";
import type {ILayerColumn, ILayerSettings} from "../../ui/modules/space/spaceeditor/ui/viewbar/LayerView";
import type {IGeoLocation} from "../../ui/widgets/input/clicktoedit/datatypes/geolocation/GeoLocationLabel";
import {compareKeyAndRefId, getDefaultCardLayout} from "../../ui/modules/settings/modules/type/form/CardLayoutEditor";
import type {IToolTipRow} from "../../ui/modules/space/spaceeditor/ui/toolbar/CardLayoutToolTip";
import {addCommasToMultiSelectFieldValues, CardLayoutToolTip} from "../../ui/modules/space/spaceeditor/ui/toolbar/CardLayoutToolTip";
import {filterModels} from "../models/filter/Filter";
import {textPartsToTextContent} from "../../ui/modules/space/spaceeditor/logic3d/managers/MSDF/TextUtils";
import type {ITextPart, IObjectWithText} from "../../ui/modules/space/spaceeditor/logic3d/managers/MSDF/TextUtils";
import type {Markup3D} from "../../ui/modules/space/spaceeditor/logic3d/elements3d/markups/abstract/Markup3D";
import type {EditableSpaceItem} from "../../ui/modules/space/spaceeditor/logic3d/elements3d/EditableSpaceItem";
import type {INotificationParams} from "../../ui/notification/Notification";
import {FilterOperator} from "../models/filter/operator/FilterOperator";
import type {Collection} from "../models/abstract/Collection";
import type {ICrossPortfolioLinkData} from "../../ui/modules/space/spaceeditor/ui/actionbar/CrossPortfolioXyicon";
import {NavigationEnum} from "../../Enums";
import type {PropertyName, spaceItemForProperties} from "../../ui/modules/abstract/sidepanel/tabs/details/field/mass/IMassInput";
import type {CaptionedItem} from "../../ui/modules/space/spaceeditor/logic3d/managers/MSDF/CaptionManager";
import {CaptionManager} from "../../ui/modules/space/spaceeditor/logic3d/managers/MSDF/CaptionManager";
import type {BoundaryManager} from "../../ui/modules/space/spaceeditor/logic3d/managers/spaceitems/BoundaryManager";
import type {XyiconManager} from "../../ui/modules/space/spaceeditor/logic3d/managers/spaceitems/XyiconManager";
import type {INumericFieldSettingsDefinition} from "../models/field/datatypes/Numeric";
import {WarningWindowV5} from "../../ui/5.0/popup/WarningWindowV5";
import type {IMultiSelectOption} from "../models/field/datatypes/MultiSelect";
import type {OrgSearchResponseDto} from "../../generated/api/search";
import type {DocumentModel} from "../models/DocumentModel";
import {isCardLayoutSupportedForField, isIconField} from "../../ui/modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {useAppStore} from "../../StateManager";
import {inheritedFeatures, getLoadingDependencyFeatures} from "./AppStateConstants";
import type {AppState} from "./AppState";
import {AppFieldActions} from "./AppFields";
import {FilterActions} from "./FilterActions";

export interface IXyiconLinkObject {
	link: Link;
	object: Xyicon;
}

export interface IFieldPropagation {
	value: string | boolean;
	modelOrId: IModel | string; // model, or model id
}

export interface IXyiconPropertyRequest {
	portfolioID: string;
	spaceID: string;
	list: IXyiconPositionList[] | IXyiconOrientationList[];
}

export interface IXyiconsOutsideCameraFrustumRow {
	"Xyicon Ref ID": string;
	"Xyicon position": string;
	"Reason for being invisible": string;
}

interface IXyiconPositionList {
	xyiconID: string;
	iconX: number;
	iconY: number;
	iconZ: number;
	linkedBoundarySpaceMapList: string[];
}

interface IXyiconOrientationList {
	xyiconID: string;
	orientation: number;
}

interface IPortfolioPermissionContext {
	individualPermissionSet: PermissionSet | null;
	groupPermissionSets: PermissionSet[];
}

interface IPortfolioPermissionResolutionCache {
	user: User | null;
	userData: Record<string, any> | null;
	portfolioPermissionListRef: unknown[] | null;
	portfolioGroupPermissionListRef: unknown[] | null;
	userVersion: number;
	permissionSetVersion: number;
	portfolioGroupVersion: number;
	individualPermissionSetByPortfolioId: Record<string, PermissionSet | null>;
	groupPermissionSetsByPortfolioId: Record<string, PermissionSet[]>;
}

const peopleFieldDataTypes = [FieldDataType.PeopleSingleChoiceList, FieldDataType.PeopleMultipleChoiceList];

export class AppActions {
	private readonly _appState: AppState;
	public readonly filterActions: FilterActions;
	private _portfolioPermissionResolutionCache: IPortfolioPermissionResolutionCache | null = null;

	constructor(appState: AppState) {
		this._appState = appState;
		this.filterActions = new FilterActions(this._appState);
	}

	public getLayout(feature: XyiconFeature) {
		return this._appState.layouts[feature];
	}

	public isFieldAssignedToItem(field: {refId: string}, item: IModel) {
		const fieldObject = this.getFieldByRefId(field.refId);
		const fieldPropagations = this.getFieldPropagations(item, this.getFieldByRefId(field.refId));

		if (fieldPropagations.length > 0 || fieldObject.default) {
			return true;
		}
		const assignedFields = this.getFieldsForType(item.typeId, item.ownFeature).filter((f) => f.value);
		const assignedFieldRefIds = assignedFields.map((f) => this.getFieldById(f.id).refId);

		return assignedFieldRefIds.some((refId) => refId === field.refId);
	}

	public isFieldValidationRuleAvailableInOrganization(feature: XyiconFeature): boolean {
		const supportedOrganizations = ["Prologis"];

		return [XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(feature) && supportedOrganizations.includes(this._appState.organization?.name);
	}

	// Returns all the fields for the item in the parameter that the user can see in the
	// "Fields" section in the details panel, regardless of their values, or if they're inherited or not
	public getAssignedFieldsFromLayoutForItem(item: IModel) {
		const fieldsFromLayout = this.getFieldsFromLayout(item.ownFeature);
		const fields: IFieldAdapter[] = fieldsFromLayout.filter((f) => this.isFieldAssignedToItem(f, item));

		return fields;
	}

	private getFieldsFromLayout(feature: XyiconFeature): IFieldAdapter[] {
		const fields: IFieldAdapter[] = [];

		const layoutSections = this.getLayout(feature)?.sections || [];

		for (const section of layoutSections) {
			for (const field of section.fields) {
				if (!fields.some((f) => f.refId === field.id)) {
					fields.push(this.getFieldByRefId(field.id));
				}
			}
		}

		return fields;
	}

	public getAssignedAndInheritedFieldsFromLayout(type: Type | null, fieldsFeature: XyiconFeature): IFieldAdapter[] {
		if (type) {
			const fieldsFromLayout = this.getFieldsFromLayout(fieldsFeature);
			const unassignedFieldRefIds = this.getFieldsForType(type.id, fieldsFeature)
				.filter((f) => !f.value)
				.map((f) => this.getFieldById(f.id).refId);

			return fieldsFromLayout.filter((f) => !unassignedFieldRefIds.includes(f.refId));
		}

		return [];
	}

	public getAssignedFieldsFromLayoutForItems(items: IModel[]) {
		let assignedFields: IFieldAdapter[] = [];

		for (const item of items) {
			assignedFields.push(...this.getAssignedFieldsFromLayoutForItem(item));
		}
		assignedFields = ArrayUtils.removeDuplicates(assignedFields);

		return assignedFields;
	}

	public getHumanFriendlyFieldValueForItem(item: IModel, field: IFieldAdapter, addPropagatedValuesIfOwnValueIsEmpty: boolean = true): string {
		let fieldValue: string | boolean = "";

		if (this.isFieldAssignedToItem(field, item as Xyicon | BoundarySpaceMap)) {
			fieldValue = this.getFieldValue(item, field.refId) ?? "";

			if (addPropagatedValuesIfOwnValueIsEmpty && (fieldValue === "" || fieldValue === null || fieldValue === undefined)) {
				fieldValue = this.getFieldPropagations(item, field)
					.map((obj) => obj.value)
					.join("\n");
			}

			// TODO: call actions.renderValue instead?

			if (
				[
					FieldDataType.User,
					FieldDataType.Type,
					FieldDataType.DateTime,
					FieldDataType.PeopleSingleChoiceList,
					FieldDataType.PeopleMultipleChoiceList,
				].includes(field.dataType)
			) {
				// Converts guid -> name (eg.: username, space type)
				fieldValue = FieldDataTypes.map[field.dataType].formatter(fieldValue);
			}

			if (Array.isArray(fieldValue)) {
				fieldValue = fieldValue.join("\n");
			}

			if (typeof fieldValue === "boolean") {
				const dataTypeSettings = field.dataTypeSettings as IBooleanFieldSettingsDefinition;

				fieldValue = fieldValue ? dataTypeSettings.displayLabelForTrue : dataTypeSettings.displayLabelForFalse;
			} else if (typeof fieldValue === "number") {
				fieldValue = MathUtils.isValidNumber(fieldValue) ? fieldValue : "";
			} else if (typeof fieldValue === "string") {
				while (fieldValue.endsWith("\n")) {
					fieldValue = fieldValue.slice(0, -1); // Trailing newlines can cause various copy-paste bugs.
				}
				fieldValue = fieldValue.replace(/\n/g, "<br>");
			}
		}

		return fieldValue;
	}

	public async selectView(view: View | Report, updateRecentViews: boolean = true) {
		if (view.ownFeature === XyiconFeature.Report && this._appState.selectedReportViewId != view.id) {
			this._appState.selectedReportViewId = view.id;
			this.selectReportViewById(view.id);
		} else if (this._appState.selectedViewId[view.itemFeature] !== view.id) {
			// Don't select the view again if not needed, as it can trigger some heavy filtering calculations on space items
			// And to make things even worse, it can result in faulty filters, for example in the following scenario:
			// Go to spaceeditor in V5
			// Open the sidegrid for xyicons
			// Apply a filter for xyicons
			// In the sidegrid, click on the "locate" button for a xyicon
			// Note that all the other xyicons have became visible as well, despite they're filtered out
			// It's because the "layer" settings are applied to the scene, but the filters are not, in this case...
			this.selectViewById(view.id);
		}
		if (updateRecentViews) {
			const updateViews = async () => {
				if (useAppStore.getState().isPortfolioSwitchRequestInProgress) {
					setTimeout(() => {
						updateViews();
					}, 20);
					return;
				}
				const {error} = await updateRecentlyUsedViews(this._appState, view.id);

				if (error) {
					console.error(error);
				}
			};

			await updateViews();
		}
	}

	public getAllViews() {
		const allViews: (View | Report)[] = [];

		for (const feature in this._appState.views) {
			allViews.push(...this._appState.views[feature]);
		}
		allViews.push(...(this._appState.reportViews ?? []));

		return allViews;
	}

	public getViews(feature: XyiconFeature) {
		return this._appState.views[feature];
	}

	public getReportViews() {
		return this._appState.reportViews ?? [];
	}

	public getAllPortfolioWideViews() {
		return [
			...this.getViews(XyiconFeature.Xyicon),
			...this.getViews(XyiconFeature.Boundary),
			...this.getViews(XyiconFeature.SpaceEditor),
			...this.getReportViews(),
		];
	}

	public selectReportViewById(viewId: string) {
		const viewMaybe = this.getReportViewById(viewId);

		if (viewMaybe) {
			this._appState.app.spaceViewRenderer.itemsHiddenNotificationMaybe?.onClose();
			this._appState.selectedReportViewId = viewId;
			this.pushToTouchedViews(viewId);
			this._appState.app.transport.services.localStorage.set(
				this._appState.getKeyForLocalStorageView(this._appState.organizationIdByAuthData, XyiconFeature.Report),
				viewMaybe.id,
			);
		}
	}

	public selectViewById(viewId: string) {
		const viewMaybe = this.getViewById(viewId);
		const prevSelectedViewMaybe = viewMaybe ? this.getSelectedView(viewMaybe?.itemFeature) : null;

		if (viewMaybe) {
			this._appState.app.spaceViewRenderer.itemsHiddenNotificationMaybe?.onClose();
			this._appState.selectedViewId[viewMaybe.itemFeature] = viewId;
			this.pushToTouchedViews(viewId);
			this._appState.app.transport.services.localStorage.set(
				this._appState.getKeyForLocalStorageView(this._appState.organizationIdByAuthData, viewMaybe.itemFeature),
				viewMaybe.id,
			);
		}

		if (prevSelectedViewMaybe?.itemFeature === XyiconFeature.SpaceEditor && (!viewMaybe || viewMaybe !== prevSelectedViewMaybe)) {
			// this needs to be outside the "if" above in case the default view is selected
			this.onViewSelected();
		}
	}

	public getKeyForLocalStorageSelectedPortfolio(
		userId: string = this._appState.user?.id,
		organizationId: string = this._appState.organizationIdByAuthData,
	) {
		return `srv4-org-${userId}-${organizationId}-selectedportfolio`;
	}

	private pushToTouchedViews(viewId: string) {
		const touchedViews = this._appState.touchedViews;
		const index = touchedViews.indexOf(viewId);

		if (index > -1) {
			touchedViews.splice(index, 1);
		}
		touchedViews.push(viewId);
	}

	// opens up the space that contains the item, and zooms to the item defined by its id
	public navigateToSpaceItemById(
		itemId: string,
		feature: XyiconFeature.Xyicon | XyiconFeature.Boundary | XyiconFeature.Markup,
		selectItem: boolean = false,
	) {
		const item = this.getBoundarySpaceMapById(itemId) || (this.getFeatureItemById(itemId, feature) as Xyicon | Boundary | BoundarySpaceMap | Markup);

		if (item) {
			this.navigateToSpaceItem(item, selectItem);
		} else {
			console.warn(`Item not found: ${itemId}`);
		}
	}

	public navigateToSpaceItem(item: Boundary | BoundarySpaceMap | Xyicon | Markup, selectItem: boolean = false) {
		let portfolioRefId = this._appState.activePortfolioRefId;
		const spaceIdBeforeNavigation = this._appState.space?.id; // store the spaceId in the moment of clicking the Locate button to know if the item is in that space or in another one

		if (item.portfolioId && item.portfolioId !== this._appState.activePortfolioId) {
			const portfolioMaybe = this._appState.actions.getPortfolioById(item.portfolioId);
			if (portfolioMaybe) {
				portfolioRefId = portfolioMaybe.refId;
			}
		}

		return new Promise<void>((resolve, reject) => {
			if (!item.space) {
				console.warn("It seems user has no permissions to access the item's space.");
				resolve();
			} else {
				if (this._appState.currentUIVersion === "5.0") {
					selectWorkspaceViewTabForFeature(this._appState, XyiconFeature.SpaceEditor, true);
				}

				this._appState.app.navigation.goApp({
					portfolioRefId,
					mainNav: NavigationEnum.SPACE,
					subNavs: [item.space.refId],
				});

				const {spaceViewRenderer} = this._appState.app;

				// If we navigate to another space item, exit photosphereviewer mode...
				const {photoSphereSceneManager, photoSphereManager} = this._appState.app.graphicalTools;
				photoSphereSceneManager.viewMode = "normal";
				photoSphereManager.activePhotoSphereMaybe = null;
				//

				const focusAfterSpaceLoad = () => {
					const {spaceLoadStarted, spaceLoadReady} = spaceViewRenderer.signals;

					const focusOnItem = () => {
						spaceLoadReady.remove(focusOnItem);
						spaceViewRenderer.toolManager.cameraControls.focusOn(item, selectItem, resolve);
					};

					spaceLoadReady.add(focusOnItem);

					let spaceLoadStartedCounter = 0;
					const handleSpaceLoadStart = (spaceId: string) => {
						spaceLoadStartedCounter++;
						if (spaceLoadStartedCounter > 1) {
							spaceLoadStarted.remove(handleSpaceLoadStart);
							spaceLoadReady.remove(focusOnItem);
						}
					};

					spaceLoadStarted.add(handleSpaceLoadStart);
				};

				if (item.spaceId === spaceIdBeforeNavigation) {
					if (spaceViewRenderer.isMounted) {
						spaceViewRenderer.toolManager.cameraControls.focusOn(item, selectItem, resolve);
					} else {
						focusAfterSpaceLoad();
					}
				} else {
					focusAfterSpaceLoad();
					const space = this.getSpaceById(item.spaceId);
					this._appState.app.navigation.goApp({
						mainNav: NavigationEnum.SPACE,
						subNavs: [space.refId],
					});
				}
			}
		});
	}

	private renderOwnAndInheritedFieldValues(model: IModel, fieldRefId: string): string[] {
		return this.renderValues(model, fieldRefId)
			.flat(2) // flat(2) -> for inherited multiselect, and things like that
			.filter((val) => !!val);
	}

	private renderOwnAndInheritedFieldValuesAsCSV(model: IModel, fieldRefId: string): string {
		return this.renderOwnAndInheritedFieldValues(model, fieldRefId).join(", ");
	}

	private renderOwnAndInheritedCaptionFieldValues(model: IModel, field: IFieldAdapter): string[] {
		if (![FieldDataType.PeopleSingleChoiceList, FieldDataType.PeopleMultipleChoiceList].includes(field.dataType)) {
			return this.renderOwnAndInheritedFieldValues(model, field.refId);
		}

		// In captions we only show the name of the user, or the email address if there's no name
		const renderUserCaptionValue = (userMaybe?: User) => userMaybe?.fullName || userMaybe?.email || "";

		return this.getValueForFormatting(model, field)
			.flatMap((value) => (Array.isArray(value) ? value : [value]))
			.map((userMaybe) =>
				renderUserCaptionValue(typeof userMaybe === "string" ? this.getFeatureItemById<User>(userMaybe, XyiconFeature.User) : (userMaybe as User)),
			)
			.filter((value) => !!value);
	}

	public getCaptionFieldValues(captionFields: IFieldAdapter[], modelData: IModel): ITextPart[] {
		if (!modelData || captionFields.length === 0) {
			return [];
		}

		const modelD = (modelData as BoundarySpaceMap).isBoundarySpaceMap ? (modelData as BoundarySpaceMap).parent : modelData;
		const itemType = modelData.ownFeature === XyiconFeature.Boundary ? "boundary" : "xyicon";
		const individualCaptionStyles =
			this.getSelectedView(XyiconFeature.SpaceEditor).spaceEditorViewSettings.captions[itemType].individualCaptionStyles ?? {};

		return captionFields
			.map((captionField) => ({
				content: this.renderOwnAndInheritedCaptionFieldValues(modelD, captionField).toSorted(StringUtils.sortIgnoreCase).join("\n"),
				style: individualCaptionStyles[captionField.refId] ?? {},
			}))
			.filter((textPart) => !!textPart.content);
	}

	public async updateSpaceEditorCaptions(feature: XyiconFeature, affectedItems?: CaptionedItem[]) {
		const spaceViewRenderer = this._appState.app.spaceViewRenderer;

		if (spaceViewRenderer.isMounted) {
			if (feature === XyiconFeature.Xyicon || feature === XyiconFeature.Boundary) {
				if (feature === XyiconFeature.Boundary) {
					const boundarySpaceMaps = affectedItems ? affectedItems.filter((item) => item.spaceItemType === "boundary") : undefined;
					await spaceViewRenderer.boundaryManager.captionManager.updateCaptions(boundarySpaceMaps);
				}
				// boundary captions can affect xyicons, but not the other way around
				const xyicons = affectedItems ? affectedItems.filter((item) => item.spaceItemType === "xyicon") : undefined;
				await spaceViewRenderer.xyiconManager.captionManager.updateCaptions(xyicons);
			}
		}
	}

	// coreAffectedIds -> add the items that were recently added, deleted, modified, etc.
	// this functions adds the linked items automatically, you don't need to add them manually
	public updateSpaceEditorCaptionsForAffectedAndLinkedItems(feature: XyiconFeature, coreAffectedItems: CaptionedItem[]) {
		const spaceViewRenderer = this._appState.app.spaceViewRenderer;

		if (spaceViewRenderer.isMounted && [XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(feature)) {
			const itemManager = spaceViewRenderer.getItemManager(feature);
			if (itemManager) {
				const affectedItems = this.extendAffectedItemsWithLinkedItems(coreAffectedItems);

				return this.updateSpaceEditorCaptions(feature, affectedItems);
			}
		}
	}

	public extendAffectedItemsWithLinkedItems(coreAffectedItems: CaptionedItem[]): CaptionedItem[] {
		const spaceViewRenderer = this._appState.app.spaceViewRenderer;
		const affectedItems = coreAffectedItems.filter((item) => !!item);

		const linkedXyicons: Set<Xyicon3D> = new Set<Xyicon3D>();
		const linkedBoundarySpaceMaps: Set<BoundarySpaceMap3D> = new Set<BoundarySpaceMap3D>();

		for (const affectedItem of affectedItems) {
			const xyiconLinks =
				affectedItem.modelData.ownFeature === XyiconFeature.Xyicon
					? this.getLinksXyiconXyicon(affectedItem.id)
					: this.getLinksXyiconsForBoundary((affectedItem.modelData as BoundarySpaceMap).parent.id);

			for (const link of xyiconLinks) {
				const linkedXyicon3D = spaceViewRenderer.xyiconManager.getItemById(link.object.id) as Xyicon3D;
				if (linkedXyicon3D) {
					linkedXyicons.add(linkedXyicon3D);
				}
			}

			const boundaryLinks =
				affectedItem.modelData.ownFeature === XyiconFeature.Xyicon
					? this.getLinksXyiconBoundary(affectedItem.id)
					: this.getLinksBoundaryBoundary((affectedItem.modelData as BoundarySpaceMap).parent.id, true);

			for (const link of boundaryLinks) {
				const boundarySpaceMapIds = [...link.object.boundarySpaceMaps].map((b) => b.id);

				for (const boundarySpaceMapId of boundarySpaceMapIds) {
					const linkedBoundarySpaceMap3D = spaceViewRenderer.boundaryManager.getItemById(boundarySpaceMapId) as BoundarySpaceMap3D;
					if (linkedBoundarySpaceMap3D) {
						linkedBoundarySpaceMaps.add(linkedBoundarySpaceMap3D);
					}
				}
			}
		}
		affectedItems.push(...linkedXyicons, ...linkedBoundarySpaceMaps);

		return ArrayUtils.removeDuplicates(affectedItems);
	}

	public updateSpaceEditorFilterState(filterState?: IFilterState, updateCaptions: boolean = true, addFullscreenLoaderText: boolean = true) {
		const spaceViewRenderer = this._appState.app.spaceViewRenderer;

		if (spaceViewRenderer.isMounted) {
			return spaceViewRenderer.spaceItemController.updateFilterState(filterState, updateCaptions, undefined, addFullscreenLoaderText);
		}

		return Promise.resolve();
	}

	public onViewSelected() {
		const spaceViewRenderer = this._appState.app.spaceViewRenderer;

		if (spaceViewRenderer.isMounted) {
			const {xyiconManager, boundaryManager} = spaceViewRenderer;

			const selectedView = this.getSelectedView(XyiconFeature.SpaceEditor);

			const spaceEditorViewSettings = selectedView.spaceEditorViewSettings;
			const {background} = spaceEditorViewSettings.layers;

			spaceViewRenderer.tileManager.container.visible = !background.isHidden;

			xyiconManager.onFormattingRulesModified();
			boundaryManager.onFormattingRulesModified();
			// When switching views we must apply the *current* view state (which may include unsaved changes),
			// otherwise we break the expected behavior where unsaved settings remain until the user saves/resets.
			// View-to-view bleed is handled by re-applying the selected view's state to the renderer here.
			const currentFilters = selectedView.filters;

			this.updateSpaceEditorFilterState(currentFilters, false, false).then(() => {
				this.updateSpaceEditorCaptions(XyiconFeature.Boundary);
			});
		}
	}

	public getList<T extends IModel>(feature: XyiconFeature): T[] {
		return this._appState.lists[feature].array;
	}

	public getLinkFromMsdfTextInstanceId(objectWithText: IObjectWithText, instanceId: number): string | null {
		const textContent = textPartsToTextContent(objectWithText.text);
		const matchAll = this.getStartIndexAndContentOfUrlsInText(textContent);
		const visibleCharactersInTextContent = textContent.replace(/\s+/g, ""); // Remove all spaces, new lines, etc

		// Adjust indices for the stripped text
		let lastStartIndex = 0;

		for (const match of matchAll) {
			match.start = visibleCharactersInTextContent.indexOf(match.content, lastStartIndex);
			lastStartIndex = match.start + 1;
		}

		const instanceIdRangeOfText = {
			first: objectWithText.textInstanceIds[0],
			last: objectWithText.textInstanceIds[objectWithText.textInstanceIds.length - 1],
		};
		const indexOfCharacter = instanceId - instanceIdRangeOfText.first;
		const matchAtIndex = matchAll.find((match) => match.start <= indexOfCharacter && indexOfCharacter < match.start + match.content.length);

		if (matchAtIndex) {
			return matchAtIndex.content;
		}

		return null;
	}

	public get URLRegex() {
		return /(((\b(https?):\/\/)|(\bwww\.))[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
	}

	public getStartIndexAndContentOfUrlsInText(text: string): {start: number; content: string}[] {
		const ret: {start: number; content: string}[] = [];

		const urlRegex = this.URLRegex;

		const matchAll = [...text.matchAll(urlRegex)];

		for (const match of matchAll) {
			ret.push({
				start: match.index,
				content: match[0],
			});
		}

		return ret;
	}

	public getURLsInTextInputs(value: any) {
		const regex = this.URLRegex;

		let match = ArrayUtils.removeDuplicates(value?.match?.(regex));
		let final = value;

		match?.forEach((url: string) => {
			const newTabURL = url.includes("//") ? url : `//${url}`;

			final = newTabURL;
		});

		return final;
	}

	public async deleteUsers(users: User[], newOwner?: User) {
		const transport = this._appState.app.transport;
		const params: DeleteUsersRequest = {
			userIDList: users.map((user) => ({userIDToBeDeleted: user.id, newSharedObjectOwnerUserID: newOwner?.id})),
		};

		const {result, error} = await transport.requestForOrganization<DeletedUsersDto>({
			url: "users/delete",
			method: XHRLoader.METHOD_DELETE,
			params,
		});

		const deletedList = result?.deletedUserList.map((item: DeletedUserInfoDto) => item.userID);

		this.checkResultOfDelete(users, deletedList, XyiconFeature.User);

		return {result, error};
	}

	public onDeletePortfolio(portfolioName: string, isActivePortfolio: boolean) {
		const deletePortolfioNotificationParams: INotificationParams = {
			type: NotificationType.Message,
			title: `${portfolioName} Portfolio has been deleted.`,
			lifeTime: Infinity,
			cancelable: false,
			buttonLabel: "Go to Portfolios",
			backdropDisable: true,
			onActionButtonClick: () => this.goToPortfolios(),
			description: "Go to Portfolios will take you to the Portfolios module.",
		};

		if (isActivePortfolio && this._appState.selectedFeature !== XyiconFeature.Portfolio && portfolioName) {
			notify(this._appState.app.notificationContainer, deletePortolfioNotificationParams);
		}
	}

	public goToPortfolios() {
		const portfolioList = this.getList(XyiconFeature.Portfolio);
		const currentView = this.getSelectedView(XyiconFeature.Portfolio);
		const filteredItems = filterModels(portfolioList, currentView.filters, this._appState, XyiconFeature.Portfolio);
		const transport = this._appState.app.transport;
		const relevantArray = filteredItems.length > 0 ? filteredItems : portfolioList.length > 0 ? portfolioList : [];

		if (relevantArray.length > 0) {
			const portfolioMaybe = this._appState.actions.getPortfolioById(relevantArray[0].id);
			if (portfolioMaybe) {
				this._appState.app.navigation.goApp({
					portfolioRefId: portfolioMaybe.refId,
					mainNav: NavigationEnum.PORTFOLIOS,
				});
			}
		} else {
			transport.appState.actions.clearPortfolioLists();
		}
	}

	public async deleteItems<T extends IModel>(items: T[], feature: XyiconFeature, apiNameAddition: "" | "boundaryspacemaps" = "") {
		const transport = this._appState.app.transport;
		const result = await transport.services.feature.deleteItems(items, feature, apiNameAddition);

		this.checkResultOfDelete(items, result?.deletedList, feature);

		if (apiNameAddition === "boundaryspacemaps") {
			for (const item of items as unknown as BoundarySpaceMap[]) {
				item.parent.removeBoundarySpaceMaps([item]);
			}
		}

		switch (feature) {
			case XyiconFeature.Space:
				{
					const deletedList = result as SpaceDeleteDto[];

					this.onSpacesDeleted(deletedList);
				}
				break;
			case XyiconFeature.SpaceVersion:
				{
					const deletedList = result as SpaceVersionsDeleteDto[];

					const spaceVersionIds = deletedList.map((d) => d.spaceVersionID);

					this.applyDelete(spaceVersionIds, XyiconFeature.SpaceVersion);

					const spaces = this.getList<Space>(XyiconFeature.Space);

					for (const space of spaces) {
						const spaceFilesToKeep = space.spaceFiles.filter((sF) => !spaceVersionIds.includes(sF.spaceVersionId));

						space.applyData({...space.data, spaceFiles: spaceFilesToKeep.map((sF) => sF.data)});
					}
				}
				break;
			case XyiconFeature.Portfolio:
				{
					if (this.getList<Portfolio>(XyiconFeature.Portfolio).length < 1) {
						this.clearPortfolioLists();
					}
				}
				break;
		}

		return result;
	}

	private async checkResultOfDelete<T extends IModel>(items: T[], deletedList: string[], feature: XyiconFeature) {
		if (deletedList) {
			this.applyDelete(deletedList, feature);

			if (items.length !== deletedList.length) {
				const itemsNotDeleted: T[] = [];

				for (const item of items) {
					if (!deletedList.includes(item?.id)) {
						itemsNotDeleted.push(item);
					}
				}

				if (itemsNotDeleted.length > 0) {
					if (!itemsNotDeleted.every((item) => (item as unknown as Markup).type === MarkupType.Photo360)) {
						const WarningWindowModul = this._appState.currentUIVersion === "5.0" ? WarningWindowV5 : WarningWindow;
						await WarningWindowModul.open(`${itemsNotDeleted.length} item(s) couldn't be deleted, because they're being used by other components.`);
					}
				}
			}
		}
	}

	public clearPortfolioLists() {
		const portfolioLists = [
			XyiconFeature.Xyicon,
			XyiconFeature.Space,
			XyiconFeature.SpaceVersion,
			XyiconFeature.Markup,
			XyiconFeature.Boundary,
			XyiconFeature.Link,
			XyiconFeature.PortfolioDocument,
		];

		for (const feature of portfolioLists) {
			this._appState.lists[feature].clear();
		}

		this.filterActions.clearCache();
		this._appState.app.transport.services.feature.clearPromises();
	}

	public getUnplottedXyicons() {
		// Unplotted xyicons don't have spaceIds
		return this.getList(XyiconFeature.Xyicon).filter((xyicon: Xyicon) => !xyicon.spaceId && !xyicon.isEmbedded);
	}

	public getCurrentPortfolio() {
		return this.getPortfolioById(this._appState.relevantPortfolioId);
	}

	public getCurrentOrganization() {
		return this._appState.organizations.find((organization) => organization.id === this._appState.organizationIdByAuthData);
	}

	public getCurrentPortfolioName() {
		const portfolio = this.getCurrentPortfolio();
		return portfolio ? portfolio.name : "";
	}

	public getCurrentOrganizationOwner() {
		const organization = this.getCurrentOrganization();

		return organization ? this.renderName(organization.ownerUserID) : "";
	}

	public getCurrentOrganizationName() {
		const organization = this.getCurrentOrganization();

		return organization ? organization.name : "";
	}

	public getCurrentOrganizationId() {
		const organization = this.getCurrentOrganization();

		return organization ? organization.id : "";
	}

	public getCurrentOrganizationSettings() {
		const organization = this.getCurrentOrganization();

		if (organization) {
			return organization.settings;
		}
		return null;
	}

	public getBoundarySpaceMapById(id: string) {
		return this._appState.boundarySpaceMaps.getById(id) || null;
	}

	public getFeatureItemById<T extends IModel>(id: string, feature: XyiconFeature): T {
		return this._appState.lists[feature].getById(id) ?? this._appState.orgWideLists[feature]?.getById(id);
	}

	// NOT as efficient as getFeatureItemById, so use only when absolutely necessary!
	public getFeatureItemByRefId<T extends IModel>(refId: string, feature: XyiconFeature): T | null {
		return (
			this._appState.lists[feature].array.find((item) => item.refId === refId) ??
			this._appState.orgWideLists[feature]?.array.find((item) => item.refId === refId) ??
			null
		);
	}

	public readonly getUserByEmail = (email: string): User => {
		return this.getList<User>(XyiconFeature.User).find((u) => u.email.toLowerCase() === email.toLowerCase());
	};

	public readonly getUserIdByEmail = (email: string): string => {
		return this.getUserByEmail(email)?.id ?? "";
	};

	public readonly getUserById = (userId: string): User => {
		return this.getFeatureItemById<User>(userId, XyiconFeature.User);
	};

	public readonly isAnyItemFieldBeingUpdated = (items: IModel[], field: IFieldAdapter): boolean => {
		const {itemFieldUpdateManager} = this._appState;

		return items.some((item: IModel) => itemFieldUpdateManager.isItemFieldBeingUpdated(item.id, field.refId, item.ownFeature));
	};

	public getDocumentById(id: string): DocumentModel {
		return (
			this.getFeatureItemById<DocumentModel>(id, XyiconFeature.PortfolioDocument) ??
			this.getFeatureItemById<DocumentModel>(id, XyiconFeature.OrganizationDocument)
		);
	}

	public getSpaceById(id: string) {
		return this.getFeatureItemById<Space>(id, XyiconFeature.Space);
	}

	public getPortfolioById(id: string) {
		return this.getFeatureItemById<Portfolio>(id, XyiconFeature.Portfolio);
	}

	public getPortfolioByRefId(refId: string): Portfolio | null {
		if (!refId) {
			return null;
		}

		return this._appState.lists[XyiconFeature.Portfolio].map.refId?.[refId]?.[0] ?? null;
	}

	private addToListAbstract(data: any, feature: XyiconFeature, lists: {[key in XyiconFeature]?: Collection<any>}) {
		const item = FeatureService.createModel(data, feature, this._appState);

		if (item) {
			const list = lists[feature];

			if (list) {
				list.add(item);
			}
		}

		return item;
	}

	public addToList(data: any, feature: XyiconFeature) {
		return this.addToListAbstract(data, feature, this._appState.lists);
	}

	public addToOrgWideList(data: any, feature: XyiconFeature) {
		return this.addToListAbstract(data, feature, this._appState.orgWideLists);
	}

	public addMultipleToList(dataArray: any[], feature: XyiconFeature) {
		const items = dataArray.map((data: any) => FeatureService.createModel(data, feature, this._appState));

		if (items.length > 0 && items.length === dataArray.length) {
			const list = this._appState.lists[feature];

			if (list) {
				list.addMultiple(items);
			}
		} else if (dataArray.length > 0) {
			console.warn("Some models couldn't be created from the data...");
		}

		return items;
	}

	public applyDelete(ids: string[], feature: XyiconFeature) {
		this._appState.lists[feature].deleteByIds(ids);

		for (const id of ids) {
			this._appState.app.transport.signalR.listener.closeValidationRulesAssociatedWithItem(id);
		}
	}

	public onSpacesDeleted(spaceList: SpaceDeleteDto[]) {
		for (const deleteData of spaceList) {
			this.applyDelete(deleteData.boundaryIDList, XyiconFeature.Boundary);
			this.applyDelete(deleteData.xyiconIDList, XyiconFeature.Xyicon);
			this.applyDelete(deleteData.markupIDList, XyiconFeature.Markup);
			this.applyDelete([deleteData.spaceID], XyiconFeature.Space);
		}
	}

	public getSelectedView(feature: XyiconFeature) {
		// Find the selected view
		const selectedViewId = this._appState.selectedViewId[feature];

		if (selectedViewId) {
			const view = this.getViewById(selectedViewId);

			if (view) {
				return view;
			}
		}

		// Pick the All view or the first view
		const views = this._appState.views[feature];

		if (views) {
			const allView = views.find((view) => view.name.indexOf("All") === 0);
			const view = allView || (views.length >= 1 ? views[0] : null);

			if (view) {
				return view;
			}
		}

		// Fall back to the default system view
		let defaultView = this._appState.defaultViews[feature];

		if (!defaultView) {
			this._appState.initAdditionalDefaultViews();
			defaultView = this._appState.defaultViews[feature];
		}

		if (defaultView) {
			return defaultView;
		}
		console.warn("view not found!");
		return null;
	}

	public isFieldDefault(refId: IFieldPointer) {
		const field = this.getFieldByRefId(refId);

		if (field) {
			return field.default;
		}
		return false;
	}

	public isFieldShownForFeature(field: IFieldAdapter, feature: XyiconFeature) {
		const isOwnField = field.feature === feature;

		if (isOwnField) {
			return true;
		} else {
			return field.displayOnLinks && this.getInheritedFeatures(feature).includes(field.feature);
		}
	}

	public isFieldValidForFeature(field: IFieldAdapter, feature: XyiconFeature) {
		if (!field) {
			// field has been removed since it had been added to the view
			return false;
		}

		// field could have been set to "displayOnLinks = false" since it had been added to the view
		return this.isFieldShownForFeature(field, feature);
	}

	public getDynamicFieldPropagations(item: IModel, field: IFieldAdapter): IFieldPropagation[] {
		if (!field.displayOnLinks || field.default) {
			return null;
		}

		if (item.ownFeature === XyiconFeature.Xyicon && field.feature === XyiconFeature.Xyicon) {
			const values: IFieldPropagation[] = [];

			this.getLinksXyiconXyicon(item.id).forEach((link) => {
				const value = this.getFieldValue(link.object, field.refId);

				if (this.isNonBlankValue(value, field)) {
					values.push({value, modelOrId: link.object});
				}
			});

			return values.length ? values : null;
		}

		if (item.ownFeature === XyiconFeature.Xyicon && field.feature === XyiconFeature.Boundary) {
			const values: IFieldPropagation[] = [];

			this.getLinksXyiconBoundary(item.id).forEach((link) => {
				const value = this.getFieldValue(link.object, field.refId);

				if (this.isNonBlankValue(value, field)) {
					values.push({value, modelOrId: link.object});
				}
			});

			return values.length ? values : null;
		}

		if (item.ownFeature === XyiconFeature.Boundary && field.feature === XyiconFeature.Boundary) {
			const values: IFieldPropagation[] = [];
			const finalItem: Boundary = ((item as BoundarySpaceMap).isBoundarySpaceMap ? (item as BoundarySpaceMap).parent : item) as Boundary;

			this.getLinksBoundaryBoundary(finalItem.id).forEach((link) => {
				const value = this.getFieldValue(link.object, field.refId);

				if (this.isNonBlankValue(value, field)) {
					values.push({value, modelOrId: link.object});
				}
			});

			return values.length ? values : null;
		}

		return null;
	}

	public getFieldRefIdsForType(typeId: string, feature: XyiconFeature) {
		let result: string[] = [];

		if (typeId) {
			const fields = this.getFieldsForType(typeId, feature).filter((field) => field.value);

			if (fields) {
				result = fields
					.map((field) => {
						const f = this.getFieldById(field.id);

						return f ? f.refId : "";
					})
					.filter((f) => !!f);
			}
		}
		return result;
	}

	public getFieldById(id: string) {
		return this._appState.fieldsById[id] || null;
	}

	public getFieldByRefId = (refId: IFieldPointer) => {
		if (!refId || !refId.indexOf) {
			return null;
		}

		return this._appState.fieldsByRef[refId] || null;
	};

	public getCatalogById = (id: string): Catalog | null => {
		if (!id) {
			return null;
		}

		return this._appState.lists[XyiconFeature.XyiconCatalog].getById(id);
	};

	public getRefIdName(feature: XyiconFeature) {
		const refId = AppFieldActions.getRefId(feature, "refId");
		const field = this.getFieldByRefId(refId);

		return field?.name || "ID";
	}

	// public getFieldAdapterByPointer(fieldPointer: IFieldPointer): IFieldAdapter
	// {
	// 	if (!fieldPointer || !fieldPointer.indexOf)
	// 	{
	// 		return null;
	// 	}
	//
	// 	const field = this._appState.fieldsByRef[fieldPointer];
	//
	// 	// TODO optimize
	// 	const isDefault = fieldPointer.indexOf("/") > -1;
	// 	if (!isDefault)
	// 	{
	// 		return this.getFieldByRefId(fieldPointer);
	// 	}
	// 	else
	// 	{
	// 		const feature = Number(fieldPointer.split("/")[0]);
	// 		const defaultFields = this._appState.defaultFields[feature] || [];
	// 		return defaultFields.find(f => f.refId === fieldPointer);
	// 	}
	// }

	// public getFieldAdapterByRefId(fieldRefId: string, feature: XyiconFeature): IFieldAdapter
	// {
	// 	const field = this.getFieldByRefId(fieldRefId);
	// 	if (field)
	// 	{
	// 		return field;
	// 	}
	// 	else
	// 	{
	// 		return (this._appState.defaultFields[feature] || []).find(f => f.refId === fieldRefId);
	// 	}
	// }

	// Same as getFieldValue but also returns propagate field values
	public getFieldValueProp(item: IModel, refId: IFieldPointer): any {
		const field = this.getFieldByRefId(refId);

		if (field) {
			if (item.ownFeature === field.feature) {
				return this.getOwnFieldValue(item, refId);
			}

			const inherited = this.getInheritedFieldValue(item, field);

			if (inherited !== null && inherited !== undefined) {
				return inherited;
			}

			const propagations = this.getDynamicFieldPropagations(item, field);

			if (propagations) {
				const firstPropagation = propagations[0];

				if (firstPropagation) {
					return this.formatValue(firstPropagation.value, field.refId);
				}
			}
		}
	}

	/**
	 * Return can be array, string, object, etc
	 * This is not the formatted string value.
	 */
	public getFieldValue(item: IModel, refId: IFieldPointer) {
		const field = this.getFieldByRefId(refId);

		if (this.isFieldHiddenByMasking(item, field)) {
			return undefined;
		}

		if (field) {
			// Boolean type field's value is undefined when permission is hidden or the toggler was never used,
			// so boolean parser's return value is false (!!param), so permission check for this type is needed to handle hidden field
			if (field.dataType === FieldDataType.Boolean) {
				const isHidden = !this._appState.user?.isAdmin && this.getFieldPermission(field, [item]) < Permission.View;

				if (isHidden) {
					return undefined;
				}
			}
			if (item.ownFeature === field.feature) {
				return this.getOwnFieldValue(item, refId);
			} else {
				return this.getInheritedFieldValue(item, field);
			}
		}
	}

	public hasOwnFieldValue(item: IModel, fieldPointer: IFieldPointer): boolean {
		const field = this.getFieldByRefId(fieldPointer);

		if (field) {
			if (item.ownFeature === field.feature) {
				if (isDefaultField(field)) {
					// not really used as we're only using this function for custom fields (so far)
					return !ObjectUtils.isNullish(item[field.getterName as keyof IModel]);
				} else {
					const fieldRefIds = this.getFieldRefIdsForType(item.typeId, item.ownFeature);

					return fieldRefIds.includes(fieldPointer);

					// Previously:
					// return if there is any non-nullish value in fieldData,
					// but this doesn't work well because fieldData[refId] can be nullish unless
					// its ever edited by a user, and we want to show blank in case the item has
					// editable field values;
					// return !ObjectUtils.isNullish(item.fieldData[field.refId]);
				}
			}
		}
		return false;
	}

	public getOwnFieldValue(item: IModel, fieldPointer: IFieldPointer) {
		const field = this.getFieldByRefId(fieldPointer);

		if (field) {
			if (item.ownFeature === field.feature) {
				if (isDefaultField(field)) {
					return (item[field.getterName as keyof IModel] as string) || "";
				} else {
					const value = item.fieldData[field.refId];

					// Ensure type has this field mapped
					if (!this.isFieldMappedToType((field as Field).id, item.typeId, field.feature)) {
						return null;
					}

					return this.convertValue(value, fieldPointer);
				}
			}
		}
		return null;
	}

	public getTypesByFeature(feature: XyiconFeature) {
		return this._appState.types[feature] || [];
	}

	public getFieldPropagations(item: IModel, field: IFieldAdapter): IFieldPropagation[] {
		const finalItem = (item as BoundarySpaceMap).isBoundarySpaceMap ? (item as BoundarySpaceMap).parent : item;
		const result: IFieldPropagation[] = [];

		// Null linkedFieldData is authoritative; missing model data or undefined linkedFieldData uses local inheritance.
		if ((item.data as OrgSearchResponseDto)?.linkedFieldData !== undefined) {
			const linkedPropagations = this.getLinkedFieldPropagations(item, field);
			result.push(...linkedPropagations);
		} else {
			const inheritedPropagation = this.getInheritedFieldPropagation(finalItem, field);

			if (inheritedPropagation) {
				result.push(inheritedPropagation);
			}
			const dynamicPropagations = this.getDynamicFieldPropagations(finalItem, field);

			if (dynamicPropagations) {
				result.push(...dynamicPropagations);
			}
		}

		return result;
	}

	// Eg.: finds field value for the Xyicon's Portfolio object, if item is a Xyicon fieldRefId refers to a portfolio field
	private getInheritedFieldValue(item: IModel, field: IFieldAdapter): string | null {
		const inheritedObject = this.findInheritedObject(item, field.feature);

		if (inheritedObject) {
			return this.getFieldValue(inheritedObject, field.refId);
		}
		return null;
	}

	private getInheritedFieldPropagation(item: IModel, field: IFieldAdapter): IFieldPropagation {
		const inheritedObject = this.findInheritedObject(item, field.feature);

		if (inheritedObject) {
			const value = this.isFieldHiddenByMasking(inheritedObject, field) ? undefined : this.getOwnFieldValue(inheritedObject, field.refId);
			const hasValue = this.isNonBlankValue(value, field);

			if (hasValue) {
				return {
					value,
					modelOrId: inheritedObject,
				};
			}
		}
		return null;
	}

	private getLinkedFieldPropagations(item: IModel, field: IFieldAdapter): IFieldPropagation[] {
		const linkedFieldDataMaybe = (item as Xyicon | Boundary | Space | Catalog | Portfolio).linkedFieldData;
		const result: IFieldPropagation[] = [];

		if (linkedFieldDataMaybe) {
			if (linkedFieldDataMaybe[field.refId]) {
				const linkedFieldDataForField = linkedFieldDataMaybe[field.refId] as {value: string; objectID: string}[];
				// eg.:
				// linkedFieldData: {
				//   "f7": [
				// 		{ value: "Commissary", objectID: "2451DD98-3161-4B1D-8C31-75AF009A6C68"}
				//   ],
				//   "f8": [
				// 		{ value: "NJA", objectID: "E3AE1398-C272-47D3-ACAB-B5445B1570A4"}
				//   ]
				// }
				for (const linkedData of linkedFieldDataForField) {
					const linkedObjectMaybe = this.getFeatureItemById<IModel>(linkedData.objectID, field.feature);

					// If the linked object is not loaded, we can't determine its catalog, so we keep the value
					if (linkedObjectMaybe && this.isFieldHiddenByMasking(linkedObjectMaybe, field)) {
						continue;
					}

					result.push({
						value: linkedData.value,
						modelOrId: linkedData.objectID,
					});
				}
			}
		}

		return result;
	}

	private isNonBlankValue(value: any, field: IFieldAdapter) {
		// Note: we could just check the value itself if it's an array, without checking field.dataType
		// but this allows more refined control based on the field
		// UPDATE: After some investigation: No, we can't just check the value itself if it's an array. Because for SingleChoiceLists,
		// it's a string, and value?.length still works like magic this way, although it's not an array.

		if (
			[
				FieldDataType.SingleChoiceList,
				FieldDataType.MultipleChoiceList,
				FieldDataType.PeopleSingleChoiceList,
				FieldDataType.PeopleMultipleChoiceList,
			].includes(field.dataType)
		) {
			if (Array.isArray(value) || typeof value === "string") {
				const count = value?.length;

				return count > 0;
			} else if (field.dataType === FieldDataType.PeopleSingleChoiceList) {
				return !!value; // value is either instanceof User, or null here
			}
		}

		return !StringUtils.isBlank(value);
	}

	public findInheritedObject(item: IModel, feature: XyiconFeature) {
		if (feature === XyiconFeature.Xyicon || feature === XyiconFeature.XyiconCatalog) {
			if (item.catalogId) {
				return this.getFeatureItemById(item.catalogId, XyiconFeature.XyiconCatalog);
			}
		} else if (feature === XyiconFeature.Portfolio) {
			if (item.portfolioId) {
				return this.getFeatureItemById(item.portfolioId, XyiconFeature.Portfolio);
			}
		} else if (feature === XyiconFeature.Space) {
			if (item.spaceId) {
				return this.getFeatureItemById(item.spaceId, XyiconFeature.Space);
			}
		}
		return null;
	}

	public getBoundariesBySpace(spaceId: string) {
		const boundariesInPortfolio = this.getList<Boundary>(XyiconFeature.Boundary);

		return boundariesInPortfolio.filter(
			(boundary: Boundary) => !![...boundary.boundarySpaceMaps].find((boundarySpaceMap: BoundarySpaceMap) => boundarySpaceMap.spaceId === spaceId),
		);
	}

	public getXyiconsBySpace(spaceId: string) {
		const xyiconsInPortfolio = this.getList<Xyicon>(XyiconFeature.Xyicon);

		return xyiconsInPortfolio.filter((xyicon: Xyicon) => xyicon.spaceId === spaceId);
	}

	public listXyiconsOutsideCameraFrustum(): IXyiconsOutsideCameraFrustumRow[] {
		const spaceViewRenderer = this._appState.app.graphicalTools?.spaceViewRenderer;

		if (!spaceViewRenderer?.isMounted) {
			console.log("Please open a space to use this function");
			return [];
		}

		const cameraControls = spaceViewRenderer.toolManager.cameraControls;
		const activeCamera = cameraControls.activeCamera;
		const xyiconLayers = this.getSelectedView(XyiconFeature.SpaceEditor)?.spaceEditorViewSettings?.layers?.xyicon?.included;
		const floorplanZ = spaceViewRenderer.spaceOffset.z;
		const rows = this.getXyiconsBySpace(spaceViewRenderer.space.id)
			.filter((xyicon: Xyicon) => {
				const typeName = xyicon.typeName || this.getCatalogById(xyicon.catalogId)?.typeName;

				return !xyiconLayers?.[typeName]?.isHidden;
			})
			.map((xyicon: Xyicon) => {
				const {position} = xyicon;
				const isBelowFloorplan = position.z < floorplanZ;
				const isPointVisibleForCamera = cameraControls.isPointVisibleForCamera(position);

				if (isPointVisibleForCamera && !isBelowFloorplan) {
					return null;
				}

				const projectedPoint = new Vector3(position.x, position.y, position.z).project(activeCamera);
				const reasons: string[] = [];

				if (isBelowFloorplan) {
					reasons.push("Below floorplan");
				}
				if (position.z > activeCamera.position.z) {
					reasons.push("Above camera");
				}
				if (!isPointVisibleForCamera && reasons.length === 0) {
					reasons.push("Outside camera frustum");
				}

				return {
					"Xyicon Ref ID": xyicon.refId,
					"Xyicon position": this.formatXyiconPositionForCameraFrustumList(position),
					"Reason for being invisible": reasons.join(", "),
				};
			})
			.filter((row: IXyiconsOutsideCameraFrustumRow | null): row is IXyiconsOutsideCameraFrustumRow => !!row)
			.sort((a, b) => StringUtils.sortIgnoreCase(a["Xyicon Ref ID"], b["Xyicon Ref ID"]));

		console.table(rows, ["Xyicon Ref ID", "Xyicon position", "Reason for being invisible"]);

		return rows;
	}

	private formatXyiconPositionForCameraFrustumList(position: {x?: number; y?: number; z?: number}) {
		const formatNumber = (value: number = 0) => (Number.isInteger(value) ? `${value}` : `${Number(value.toFixed(3))}`);

		return `(${formatNumber(position.x)}, ${formatNumber(position.y)}, ${formatNumber(position.z)})`;
	}

	public getMarkupsBySpace(spaceId: string) {
		const markupsInPortfolio = this.getList(XyiconFeature.Markup); //  all the markups in the portfolio

		return markupsInPortfolio.filter((markup: Markup) => markup.spaceId === spaceId) as Markup[];
	}

	public getCrossPortfolioLinksXyiconXyicon(xyiconId: string): ICrossPortfolioLinkData[] {
		if (!this._appState.user?.isAdmin) {
			return [];
		}

		const result: ICrossPortfolioLinkData[] = [];
		const links = this._appState.lists[XyiconFeature.Link] as Collection<Link>;

		const fromLinks = links.map.objectAID?.[xyiconId];
		const toLinks = links.map.objectBID?.[xyiconId];

		fromLinks?.forEach((link) => {
			if (
				link.objectBPortfolioID !== link.objectAPortfolioID &&
				link.objectALinkType === LinkType.Xyicon &&
				link.objectBLinkType === LinkType.Xyicon
			) {
				result.push({
					link,
					crossPortfolioXyiconId: link.objectBID,
					otherPortfolioId: link.objectBPortfolioID,
					otherPortId: link.objectBPortID,
					onePortId: link.objectAPortID,
				});
			}
		});
		toLinks?.forEach((link) => {
			if (
				link.objectAPortfolioID !== link.objectBPortfolioID &&
				link.objectALinkType === LinkType.Xyicon &&
				link.objectBLinkType === LinkType.Xyicon
			) {
				result.push({
					link,
					crossPortfolioXyiconId: link.objectAID,
					otherPortfolioId: link.objectAPortfolioID,
					otherPortId: link.objectAPortID,
					onePortId: link.objectBPortID,
				});
			}
		});

		return result;
	}

	// Returns with xyicon-xyicon links from the same portfolio
	public getLinksXyiconXyicon(xyiconId: string): IXyiconLinkObject[] {
		const result: IXyiconLinkObject[] = [];
		const xyiconsInPortfolio = this._appState.lists[XyiconFeature.Xyicon] as Collection<Xyicon>;
		const preloadedXyiconsInOrganization = this._appState.orgWideLists[XyiconFeature.Xyicon];
		const links = this._appState.lists[XyiconFeature.Link] as Collection<Link>;

		const fromLinks = links.map.objectAID?.[xyiconId];
		const toLinks = links.map.objectBID?.[xyiconId];

		fromLinks?.forEach((link) => {
			const otherXyicon = xyiconsInPortfolio.getById(link.objectBID) ?? preloadedXyiconsInOrganization.getById(link.objectBID);

			if (otherXyicon) {
				result.push({link, object: otherXyicon});
			}
		});
		toLinks?.forEach((link) => {
			const otherXyicon = xyiconsInPortfolio.getById(link.objectAID) ?? preloadedXyiconsInOrganization.getById(link.objectAID);

			if (otherXyicon) {
				result.push({link, object: otherXyicon});
			}
		});

		return result;
	}

	public getLinksXyiconBoundary(xyiconId: string): {link: Link; object: Boundary}[] {
		const result: {link: Link; object: Boundary}[] = [];
		const boundaryIds = new Set<string>();

		const links = this._appState.lists[XyiconFeature.Link];

		const fromLinks = links.map.objectAID?.[xyiconId];
		const toLinks = links.map.objectBID?.[xyiconId];

		fromLinks?.forEach((link) => {
			// we're searching for the boundaries that match that
			const boundary = this.findBoundaryForBoundarySpaceMap(link.objectBID);

			if (boundary && !boundaryIds.has(boundary.id)) {
				boundaryIds.add(boundary.id);
				result.push({link, object: boundary});
			}
		});
		toLinks?.forEach((link) => {
			// we're searching for the boundaries that match that
			const boundary = this.findBoundaryForBoundarySpaceMap(link.objectAID);

			if (boundary && !boundaryIds.has(boundary.id)) {
				boundaryIds.add(boundary.id);
				result.push({link, object: boundary});
			}
		});

		return result;
	}

	public getLinksXyiconsForBoundary(boundaryId: string): {link: Link; object: Xyicon}[] {
		const result: IXyiconLinkObject[] = [];
		const xyiconIds = new Set<string>();

		const boundary = this.getFeatureItemById<Boundary>(boundaryId, XyiconFeature.Boundary);

		if (boundary) {
			const links = this._appState.lists[XyiconFeature.Link]; // contains all the links - orgwide, because they're added in "extendLocalOrgSearchModel"
			const xyiconsInPortfolio = this._appState.lists[XyiconFeature.Xyicon];
			const preloadedXyiconsInOrganization = this._appState.orgWideLists[XyiconFeature.Xyicon];

			boundary.boundarySpaceMaps.forEach((boundarySpaceMap) => {
				const fromLinks = links.map.objectAID?.[boundarySpaceMap.id];
				const toLinks = links.map.objectBID?.[boundarySpaceMap.id];

				fromLinks?.forEach((link) => {
					const xyicon = xyiconsInPortfolio.getById(link.objectBID) ?? preloadedXyiconsInOrganization.getById(link.objectBID);

					if (xyicon && !xyiconIds.has(xyicon.id)) {
						xyiconIds.add(boundary.id);
						result.push({link, object: xyicon});
					}
				});
				toLinks?.forEach((link) => {
					const xyicon = xyiconsInPortfolio.getById(link.objectAID) ?? preloadedXyiconsInOrganization.getById(link.objectAID);

					if (xyicon && !xyiconIds.has(xyicon.id)) {
						xyiconIds.add(boundary.id);
						result.push({link, object: xyicon});
					}
				});
			});
		}

		return result;
	}

	public getLinksBoundaryBoundary(boundaryId: string, bidirectional: boolean = false): {link: Link; object: Boundary}[] {
		const result: {link: Link; object: Boundary}[] = [];
		const boundaryIds = new Set<string>();

		const boundaryA = this.getFeatureItemById<Boundary>(boundaryId, XyiconFeature.Boundary);
		const boundarySpaceMapIds = [...(boundaryA?.boundarySpaceMaps ?? [])].map((bsm: BoundarySpaceMap) => bsm.id);

		if (boundarySpaceMapIds) {
			for (const boundarySpaceMapId of boundarySpaceMapIds) {
				if (bidirectional) {
					const fromLinks = this._appState.lists[XyiconFeature.Link].map.objectAID?.[boundarySpaceMapId];

					fromLinks?.forEach((link) => {
						const boundary = this.findBoundaryForBoundarySpaceMap(link.objectBID);

						if (boundary) {
							if (!boundaryIds.has(boundary.id)) {
								boundaryIds.add(boundary.id);
								result.push({link, object: boundary});
							}
						}
					});
				}

				const toLinks = this._appState.lists[XyiconFeature.Link].map.objectBID?.[boundarySpaceMapId];

				toLinks?.forEach((link) => {
					const boundary = this.findBoundaryForBoundarySpaceMap(link.objectAID);

					if (boundary) {
						if (!boundaryIds.has(boundary.id)) {
							boundaryIds.add(boundary.id);
							result.push({link, object: boundary});
						}
					}
				});
			}
		}

		return result;
	}

	private findBoundaryForBoundarySpaceMap(boundarySpaceMapId: string): Boundary {
		const boundarySpaceMap = this._appState.boundarySpaceMaps.getById(boundarySpaceMapId);

		return boundarySpaceMap?.parent || null;
	}

	public renderValue(item: IModel, refId: IFieldPointer): string {
		const value = this.getFieldValue(item, refId);

		// Decision: if value is null / undefined:
		// should we return "" or let this.formatValue decide? (eg. boolean)

		const formattedValue = this.formatValue(value, refId);

		if (Array.isArray(formattedValue)) {
			return formattedValue.join("\n");
		} else {
			return formattedValue;
		}
	}

	public renderValues(item: IModel, refId: IFieldPointer): any[] {
		// is it always string[] ?
		const field = this.getFieldByRefId(refId);
		const values = this.getValueForFormatting(item, field);

		return values.map((value) => this.formatValue(value, field.refId));
	}

	public formatValue(value: any, fieldPointer: IFieldPointer): string | string[] {
		const field = this.getFieldByRefId(fieldPointer);

		if (field && field.dataType) {
			const fieldDataTypeConfig = FieldDataTypes.map[field.dataType];

			if (fieldDataTypeConfig) {
				const formatter = fieldDataTypeConfig.formatter;

				if (formatter) {
					return formatter(value, field.dataTypeSettings) || "";
				}
			}
		}

		return value ?? "";
	}

	public formatValueByDataType(value: any, dataType: FieldDataType, dataTypeSettings?: any) {
		const fieldDataTypeConfig = FieldDataTypes.map[dataType];

		if (fieldDataTypeConfig) {
			const formatter = fieldDataTypeConfig.formatter;

			if (formatter) {
				return formatter(value, dataTypeSettings) || "";
			}
		}

		return value ?? "";
	}

	public convertValue(value: string, fieldPointer: IFieldPointer) {
		const field = this.getFieldByRefId(fieldPointer);

		if (field) {
			const fieldDataType = FieldDataTypes.map[field.dataType];

			if (fieldDataType) {
				const converter = fieldDataType.converter;

				if (converter) {
					return converter(value, this);
				}
			}
		}
		return value ?? "";
	}

	private getValueForFormatting(model: IModel, field: IFieldAdapter) {
		const ownValue = this.isFieldHiddenByMasking(model, field) ? undefined : this.getOwnFieldValue(model, field.refId);
		const propagations = this.getFieldPropagations(model, field);

		return [ownValue, ...propagations.map((v) => v.value)];
	}

	public getFormattingColor(model: IModel, fieldRefId: IFieldPointer): string | null {
		const field = this.getFieldByRefId(fieldRefId) as Field;

		if (field) {
			const rules = field.formattingRules;

			if (rules) {
				const values = this.getValueForFormatting(model, field);

				for (let i = 0; i < rules.length; ++i) {
					const rule = rules[i];
					const config: IFilterOperatorConfig = FilterOperators.map[rule.operator];

					for (const value of values) {
						const result = config.method(value, rule.params, field);

						if (result) {
							return rule.color;
						}
					}
				}
			}
		}

		// Nor matching rule found
		return null;
	}

	private updateFieldValuesForItem(item: IModel, fieldData: {[refId: string]: any}) {
		for (const refId in fieldData) {
			item.fieldData[refId] = fieldData[refId];
		}
	}

	private getCurrentFieldValuesForItem(item: IModel, fieldData: {[refId: string]: any}) {
		const itemFieldData: {[refId: string]: any} = {};

		for (const refId in fieldData) {
			itemFieldData[refId] = item.fieldData[refId];
		}

		return itemFieldData;
	}

	private getResultWithItemIdList(item: IModel, result: Record<string, any>) {
		if (!item?.ownFeature || !item?.id || !result) {
			return result;
		}

		const idListKey = {
			[XyiconFeature.Portfolio]: "portfolioIDList",
			[XyiconFeature.Boundary]: "boundaryIDList",
			[XyiconFeature.XyiconCatalog]: "xyiconCatalogIDList",
			[XyiconFeature.Xyicon]: "xyiconIDList",
			[XyiconFeature.Space]: "spaceIDList",
		}[item.ownFeature as XyiconFeature.Portfolio | XyiconFeature.Boundary | XyiconFeature.XyiconCatalog | XyiconFeature.Xyicon | XyiconFeature.Space];

		if (!idListKey) {
			return result;
		}

		const currentIdList = result[idListKey];
		if (Array.isArray(currentIdList) && currentIdList.length > 0) {
			return result;
		}

		return {
			...result,
			[idListKey]: [item.id],
		};
	}

	public async updateFields(items: IModel[], fieldData: {[refId: string]: any}): Promise<IResponse> {
		for (const refId in fieldData) {
			let fieldValue = fieldData[refId];
			if (Array.isArray(fieldValue) && fieldValue.length > 0) {
				if (typeof fieldValue[0].value !== "undefined") {
					fieldValue = (fieldValue as IMultiSelectOption[]).map((o: IMultiSelectOption) => o.value);
					fieldData[refId] = fieldValue;
				}
			}
		}

		const previousFieldValues: {[refId: string]: any}[] = [];

		const filteredItems = items.filter((item, index) => {
			previousFieldValues[index] = this.getCurrentFieldValuesForItem(item, fieldData);
			this.updateFieldValuesForItem(item, fieldData);

			const newFieldValue = this.getCurrentFieldValuesForItem(item, fieldData);
			const hasChanged = JSON.stringify(previousFieldValues[index]) !== JSON.stringify(newFieldValue);

			if (hasChanged) {
				for (const refId in fieldData) {
					const {validationRuleFailedNotifications} = this._appState.app.transport.signalR.listener;
					const notificationKey = this._appState.itemFieldUpdateManager.getKeyForItemFieldUpdate(item.id, refId, item.ownFeature);

					if (validationRuleFailedNotifications[notificationKey]) {
						validationRuleFailedNotifications[notificationKey].onClose();
						delete validationRuleFailedNotifications[notificationKey];
					}

					this._appState.itemFieldUpdateManager.addItemFieldUpdateToUpdateList(item.id, refId, item.ownFeature);
				}
			}

			return hasChanged;
		});

		if (filteredItems.length > 0) {
			const response = await this._appState.app.transport.services.feature.updateMassFields(filteredItems, fieldData);

			const {result, error} = response;

			if (result) {
				// Is it possible that result.fieldValues !== fieldData?
				filteredItems.forEach((item) => {
					const resultWithItemIdList = this.getResultWithItemIdList(item, result);

					if (result.fieldValues) {
						this.updateFieldValuesForItem(item, result.fieldValues);
					}
					item.applyUpdate?.(resultWithItemIdList);
					if (!this._appState.app.spaceViewRenderer.isV5) {
						this.updateConditionalFormattingForFeature(item.ownFeature);
					}

					for (const refId in fieldData) {
						const field = this.getFieldByRefId(refId);

						// The ones with validation need to be handled with the help of SignalR
						if (field && !field.hasValidation) {
							this._appState.itemFieldUpdateManager.removeItemFieldUpdateFromUpdateList(item.id, refId, item.ownFeature);
						}
					}
				});

				this.updateSpotlightFormatting();

				if (filteredItems) {
					await this.updateSpaceEditorFilterState(undefined, false);
					const {spaceViewRenderer} = this._appState.app;
					if (spaceViewRenderer.isMounted) {
						const captionedItemModels = filteredItems.filter((item) => [XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(item.ownFeature));
						const captionedItems: CaptionedItem[] = captionedItemModels
							.map((item) => {
								if (item.ownFeature === XyiconFeature.Xyicon) {
									return spaceViewRenderer.getItemManager(item.ownFeature).getItemById(item.id);
								} else if (item.ownFeature === XyiconFeature.Boundary) {
									const boundarySpaceMapModels = (item as Boundary).boundarySpaceMaps;
									return [...boundarySpaceMapModels].map((bsm) => spaceViewRenderer.boundaryManager.getItemById(bsm.id));
								}
							})
							.flat()
							.filter((item) => !!item) as CaptionedItem[];
						if (captionedItems.length > 0) {
							const captionFeature = captionedItems.some((item) => item.spaceItemType === "xyicon") ? XyiconFeature.Xyicon : XyiconFeature.Boundary;
							this.updateSpaceEditorCaptionsForAffectedAndLinkedItems(captionFeature, captionedItems);
						}
					}
				}
			}
			if (error) {
				filteredItems.forEach((item, index) => {
					this.updateFieldValuesForItem(item, previousFieldValues[index]);
				});
			}

			return response;
		}

		return {
			result: "",
			error: null,
		};
	}

	public async updateMassFields(items: IModel[], fieldData: {[refId: string]: any}) {
		const fieldRefIds = Object.keys(fieldData);

		for (const refId in fieldData) {
			let fieldValue = fieldData[refId];
			if (Array.isArray(fieldValue) && fieldValue.length > 0) {
				if (typeof fieldValue[0].value !== "undefined") {
					fieldValue = (fieldValue as IMultiSelectOption[]).map((o: IMultiSelectOption) => o.value);
					fieldData[refId] = fieldValue;
				}
			}

			const fieldMaybe = this.getFieldByRefId(refId);
			if (fieldMaybe) {
				for (const item of items) {
					this._appState.itemFieldUpdateManager.addItemFieldUpdateToUpdateList(item.id, fieldMaybe.refId, item.ownFeature);
				}
			}
		}
		runInAction(() => {
			for (const item of items) {
				this.updateFieldValuesForItem(item, fieldData);
			}
		});

		let isSomeBoundary = false;
		let isSomeXyicon = false;

		const response = await this._appState.app.transport.services.feature.updateMassFields(items, fieldData);
		const {result, error} = response;

		for (const fieldRefId of fieldRefIds) {
			const fieldMaybe = this.getFieldByRefId(fieldRefId);
			// The ones with validation need to be handled with the help of SignalR
			if (fieldMaybe && !fieldMaybe.hasValidation) {
				for (const item of items) {
					this._appState.itemFieldUpdateManager.removeItemFieldUpdateFromUpdateList(item.id, fieldMaybe.refId, item.ownFeature);
				}
			}
		}

		if (result) {
			runInAction(() => {
				for (const item of items) {
					const resultWithItemIdList = this.getResultWithItemIdList(item, result);

					if (item.ownFeature === XyiconFeature.Boundary) {
						isSomeBoundary = true;
					} else if (item.ownFeature === XyiconFeature.Xyicon) {
						isSomeXyicon = true;
					}
					item.applyUpdate?.(resultWithItemIdList);
				}
			});

			if (isSomeBoundary) {
				this.updateSpaceEditorCaptions(XyiconFeature.Boundary);
				if (!this._appState.app.spaceViewRenderer.isV5) {
					this.updateConditionalFormattingForFeature(XyiconFeature.Boundary);
				}
			}
			if (isSomeXyicon) {
				// if we called it with XyiconFeature.Boundary above, there's no need to update it again,
				// because it updates the xyicon captions as well,
				// because boundary fields can cascade down to xyicons
				if (!isSomeBoundary) {
					this.updateSpaceEditorCaptions(XyiconFeature.Xyicon);
				}
				if (!this._appState.app.spaceViewRenderer.isV5) {
					this.updateConditionalFormattingForFeature(XyiconFeature.Xyicon);
				}
			}
			this.updateSpotlightFormatting();
		}

		return response;
	}

	public updateProperties(value: number, propName: PropertyName, spaceItems: SpaceItem[], distanceUnit: DistanceUnitName) {
		const {spaceViewRenderer} = this._appState.app;
		const notificationParams = {
			title: "The object you are editing is not in the field of view.",
			description: "Click Locate to bring your object into focus.",
			type: NotificationType.Message,
			buttonLabel: "Locate",
			lifeTime: Infinity,
		};

		const boundarySpaceMap3Ds: BoundarySpaceMap3D[] = [];
		const xyicon3Ds: Xyicon3D[] = [];
		const markup3Ds: Markup3D[] = [];

		if (isNaN(value)) {
			value = 0;
		}

		spaceItems.forEach((spaceItem) => {
			const model = spaceItem.modelData as Xyicon;

			if (spaceItem.spaceItemType === "xyicon") {
				if (propName === "o") {
					// change rotation
					const degree = 360 - value; // value is given CW, but we need CCW
					const rad = MathUtils.clampRadianBetween0And2PI(degree * MathUtils.DEG2RAD);

					model.setOrientation(rad);
				} else if (["px", "py", "pz"].includes(propName)) {
					// change position
					const correctedValue = MathUtils.convertDistanceToSpaceUnit(value, distanceUnit, model.space.spaceUnitsPerMeter);

					model.setPosition(
						propName === "px" ? correctedValue : model.iconX,
						propName === "py" ? correctedValue : model.iconY,
						propName === "pz" ? Math.max(correctedValue, spaceViewRenderer.spaceOffset.z) : model.iconZ,
					);

					if (!spaceViewRenderer.toolManager.cameraControls.isPointVisibleForCamera(model.position)) {
						notify(this._appState.app.notificationContainer, {
							...notificationParams,
							onActionButtonClick: () => this.navigateToSpaceItemById(model.id, model.ownFeature),
						});
					}
				}

				spaceItem.updateByModel(model as IEditableItemModel & Xyicon);
				xyicon3Ds.push(spaceItem as Xyicon3D);
			} else if (spaceItem.spaceItemType === "boundary") {
				// Boundaries
				const model = (spaceItem.modelData as BoundarySpaceMap).parent;
				const boundarySpaceMap = spaceItem.modelData as BoundarySpaceMap;

				if (propName === "o") {
					// change rotation
					const degree = 360 - value; // value is given CW, but we need CCW
					const rad = MathUtils.clampRadianBetween0And2PI(degree * MathUtils.DEG2RAD);

					const newGeometryData = THREEUtils.getRotatedVertices(boundarySpaceMap.geometryData, rad - boundarySpaceMap.orientation);

					boundarySpaceMap.setGeometryData(newGeometryData);
					boundarySpaceMap.setOrientation(rad);
				} else if (["px", "py", "pz"].includes(propName)) {
					// change position
					const correctedValue = MathUtils.convertDistanceToSpaceUnit(value, distanceUnit, model.space.spaceUnitsPerMeter);

					boundarySpaceMap.setPosition({
						x: propName === "px" ? correctedValue : boundarySpaceMap.positionX,
						y: propName === "py" ? correctedValue : boundarySpaceMap.positionY,
					});

					if (!spaceViewRenderer.toolManager.cameraControls.isPointVisibleForCamera(boundarySpaceMap.position)) {
						notify(this._appState.app.notificationContainer, {
							...notificationParams,
							onActionButtonClick: () => this.navigateToSpaceItemById(boundarySpaceMap.id, boundarySpaceMap.ownFeature),
						});
					}
				} // change dimension
				else {
					// if dimension was set to a negative value, it will return with the original width / height
					if (value > 0) {
						const correctedValue = MathUtils.convertDistanceToSpaceUnit(value, distanceUnit, model.space.spaceUnitsPerMeter);

						const dimension = boundarySpaceMap.dimension;

						boundarySpaceMap.setDimensions({
							x: propName === "dx" ? correctedValue : dimension.x,
							y: propName === "dy" ? correctedValue : dimension.y,
						});
					}
				}

				if (spaceItem) {
					(spaceItem as BoundarySpaceMap3D).updateByModel(boundarySpaceMap as IEditableItemModel & BoundarySpaceMap);
					boundarySpaceMap3Ds.push(spaceItem as BoundarySpaceMap3D);
				}
			} else {
				// Markup
				const model = spaceItem.modelData as Markup;

				if (propName === "o") {
					// change rotation
					const degree = 360 - value; // value is given CW, but we need CCW
					const rad = MathUtils.clampRadianBetween0And2PI(degree * MathUtils.DEG2RAD);

					const newGeometryData = THREEUtils.getRotatedVertices(model.geometryData, rad - model.orientation);

					// Callout markup needs this to change the point of the arrow
					if (model.type === MarkupType.Callout) {
						const newTargetData = THREEUtils.getRotatedVertex(model.target, rad - model.orientation, model.position);

						model.setTarget(newTargetData);
					}

					model.setGeometryData(newGeometryData);
					model.setOrientation(rad);
				} else if (["px", "py", "pz"].includes(propName)) {
					// change position
					const correctedValue = MathUtils.convertDistanceToSpaceUnit(value, distanceUnit, model.space.spaceUnitsPerMeter);
					const oldModelPosition = ObjectUtils.deepClone(model.position);
					const oldModelTarget = ObjectUtils.deepClone(model.target);

					model.setPosition({
						x: propName === "px" ? correctedValue : model.positionX,
						y: propName === "py" ? correctedValue : model.positionY,
					});

					// Callout markup needs this to change the point of the arrow
					if (model.type === MarkupType.Callout) {
						const deltaX = model.position.x - oldModelPosition.x;
						const deltaY = model.position.y - oldModelPosition.y;

						const newTargetX = oldModelTarget.x + deltaX;
						const newTargetY = oldModelTarget.y + deltaY;

						model.setTarget({x: newTargetX, y: newTargetY});
					}

					if (!spaceViewRenderer.toolManager.cameraControls.isPointVisibleForCamera(model.position)) {
						notify(this._appState.app.notificationContainer, {
							...notificationParams,
							onActionButtonClick: () => this.navigateToSpaceItemById(model.id, model.ownFeature),
						});
					}
				}

				(spaceItem as Markup3D).updateByModel(model as EditableSpaceItem & Markup);
				markup3Ds.push(spaceItem as Markup3D);
			}
		});

		const promises = [
			spaceViewRenderer.boundaryManager.updateItems(boundarySpaceMap3Ds, true),
			spaceViewRenderer.xyiconManager.updateItems(xyicon3Ds, true),
			spaceViewRenderer.markupManager.updateItems(markup3Ds, true),
		];

		const {spaceItemController} = spaceViewRenderer;

		if (["px", "py", "pz"].includes(propName)) {
			spaceItemController.rotationIconManager.clear();
		}

		spaceItemController.updateActionBar();
		spaceItemController.updateBoundingBox();
		spaceItemController.rotationIconManager.updateTransformations();
		spaceItemController.boundaryManager.captionManager.updateCaptions();
		spaceItemController.xyiconManager.captionManager.updateCaptions();

		return Promise.all(promises);
	}

	public formatPropValue(value: number, propName: PropertyName, item: spaceItemForProperties, distanceUnit: DistanceUnitName) {
		if (propName === "o") {
			// saved orientation is CCW, but we need CW value
			return MathUtils.clampDegreeBetween0And360(360 - value * MathUtils.RAD2DEG);
		} else {
			const spaceUnitsPerMeter = (item.modelData as Xyicon | Boundary).space?.spaceUnitsPerMeter;
			const retVal = spaceUnitsPerMeter ? MathUtils.convertDistanceFromSpaceUnit(value, distanceUnit, spaceUnitsPerMeter) : 0;

			return MathUtils.setPrecision(retVal, 2);
		}
	}

	public async updateXyiconsModel(xyiconsToBeChanged: Xyicon[], params: UpdateXyiconsModelRequest) {
		const response = await this._appState.app.transport.services.feature.updateXyiconModel(params);
		const {result, error} = response;

		if (result) {
			for (const data of result) {
				const changedXyicon = xyiconsToBeChanged.find((xyicon) => xyicon.id === data.xyiconID);
				const storedXyicon = this.getFeatureItemById<Xyicon>(data.xyiconID, XyiconFeature.Xyicon);

				changedXyicon?.applyData(data);
				if (storedXyicon !== changedXyicon) {
					storedXyicon?.applyData(data);
				}
			}
		}
		return response;
	}

	public async updateReport(report: Report) {
		const {result, error} = await this._appState.app.transport.services.feature.updateReport(report);

		if (result) {
			report.applyData(result);
		}
		return {result, error};
	}

	public updateConditionalFormattingForFeature(feature: XyiconFeature) {
		const {spaceViewRenderer} = this._appState.app;

		if (spaceViewRenderer.isV5) {
			this.updateSpotlightFormatting();
			return;
		}

		if (spaceViewRenderer.isMounted) {
			spaceViewRenderer.getItemManager(feature)?.onFormattingRulesModified();
		}
	}

	public updateSpotlightFormatting() {
		const {spaceViewRenderer} = this._appState.app;
		if (spaceViewRenderer.isMounted && spaceViewRenderer.isV5) {
			// A source field can affect either manager through parent objects or links.
			spaceViewRenderer.xyiconManager.onFormattingRulesModified();
			spaceViewRenderer.boundaryManager.onFormattingRulesModified();
		}
	}

	public getFieldTitle(fieldPointer: IFieldPointer) {
		const field = this.getFieldByRefId(fieldPointer);

		if (field) {
			return field.name || "";
		}
		return "";
	}

	// TODO Is this a good place here?
	public getBooleanFieldTitle(value: boolean) {
		return value ? "Yes" : "No";
	}

	public getValueFromPropagation(item: IModel, columnRefId: IFieldPointer) {
		const field = this.getFieldByRefId(columnRefId);

		if (field) {
			const propagations = this.getDynamicFieldPropagations(item, field);

			if (propagations) {
				const result = propagations.map((propagation) => this.formatValue(propagation.value, field.refId));

				// Add first element to the beginning
				const ownValue = this.renderValue(item, field.refId);

				if (!StringUtils.isBlank(ownValue)) {
					result.unshift(ownValue);
				}

				return result;
			}
		}

		return null;
	}

	private getFilteredInheritedArray(row: IModel, columnRefId: IFieldPointer) {
		return ([this.getValueFromPropagation(row, columnRefId)].flat(Infinity) as string[]).filter((v) => v != null);
	}

	private getSortValue(row1: IModel, row2: IModel, columnRefId: IFieldPointer) {
		const field = this.getFieldByRefId(columnRefId);

		if (!field) {
			return ["", ""];
		}
		const value1 = this.getFilteredInheritedArray(row1, columnRefId);
		const value2 = this.getFilteredInheritedArray(row2, columnRefId);
		const length1 = value1.length;
		const length2 = value2.length;

		if (length1 !== 0 || length2 !== 0) {
			const inheritedArray1 = value1;
			const inheritedArray2 = value2;

			const minArrayLength = Math.min(inheritedArray1.length, inheritedArray2.length);

			if (length2 !== 0 && length1 !== 0) {
				for (let i = 0; i < minArrayLength; i++) {
					if (inheritedArray1[i] !== inheritedArray2[i]) {
						return [inheritedArray1[i], inheritedArray2[i]];
					}
				}
				// if every checked element is the same until the min(length1,length2), then sort by the length
				return [inheritedArray1.length, inheritedArray2.length];
			} else if (length2 === 0) {
				return [inheritedArray1[0], this.getFieldValue(row2, columnRefId)];
			}
			return [this.getFieldValue(row1, columnRefId), inheritedArray2[0]];
		}

		// numeric or date or time dataType, compare them as they saved
		if ([FieldDataType.Numeric, FieldDataType.DateTime].includes(field.dataType)) {
			return [this.getFieldValue(row1, columnRefId), this.getFieldValue(row2, columnRefId)];
		}
		// if the dataType is IP, then split the IP address and compare the sections as numbers
		if (field.dataType === FieldDataType.IPAddress) {
			const ip1 = this.getFieldValue(row1, columnRefId);
			const ip2 = this.getFieldValue(row2, columnRefId);

			// if the datatype is IP and inherited DOL, the value can be null, so can't split
			if (ip1 && ip2) {
				const ip1SplitAsIPV4 = ip1.split(".");
				const ip2SplitAsIPV4 = ip2.split(".");

				if (ip1SplitAsIPV4.length > 1 && ip2SplitAsIPV4.length > 1) {
					for (let i = 0; i < ip1SplitAsIPV4.length; i++) {
						if (ip1SplitAsIPV4[i] !== ip2SplitAsIPV4[i]) {
							return [parseInt(ip1SplitAsIPV4[i]), parseInt(ip2SplitAsIPV4[i])];
						}
					}
				}
			}
			return [ip1, ip2];
		}
		// if the dataType is GeoLocation, the value is an object, compare the 'lat' or the 'long' prop
		if (field.dataType === FieldDataType.GeoLocation) {
			const geoLoc1 = this.getFieldValue(row1, columnRefId) as unknown as IGeoLocation;
			const geoLoc2 = this.getFieldValue(row2, columnRefId) as unknown as IGeoLocation;

			return geoLoc1.lat !== geoLoc2.lat ? [geoLoc1.lat, geoLoc2.lat] : [geoLoc1.long, geoLoc2.long];
		}
		return [this.renderValue(row1, columnRefId), this.renderValue(row2, columnRefId)];
	}

	public getRowsForCardLayout(queryString: string, model: IModel, disableCustomRows: boolean = false) {
		const item = model as Boundary | Xyicon | Catalog | BoundarySpaceMap | Space;

		const type = this.getTypeById(item.typeId);
		const cardLayout = type?.settings.cardLayout?.[item.ownFeature] || getDefaultCardLayout(item.ownFeature);

		const rows: IToolTipRow[] = [];

		rows.push(CardLayoutToolTip.getKeyAndValue(cardLayout["row 1"], this, item, true));
		rows.push(CardLayoutToolTip.getKeyAndValue(cardLayout["row 2"], this, item, true));
		rows.push(CardLayoutToolTip.getKeyAndValue(cardLayout["row 3"], this, item, true));

		if (disableCustomRows) {
			return rows;
		}

		// remove rows from customRows
		let customRows = this.searchModelCustomFields(item, queryString, item.ownFeature).filter((cRow) =>
			rows.every((ogRow) => !compareKeyAndRefId(ogRow.key, cRow.refId) || ogRow.value !== this.renderValue(item, cRow.refId)),
		);

		rows[0].key = "";

		// Replace the 2nd and 3rd hardcoded Row with searched custom Rows (if there are any)
		if (customRows[0]) {
			if (!rows[1].value.toLowerCase().includes(queryString)) {
				rows[1] = {
					key: customRows[0].name,
					value: addCommasToMultiSelectFieldValues(this.renderOwnAndInheritedFieldValuesAsCSV(item, customRows[0].refId), customRows[0]),
				};
			} else {
				rows[1].key = "";
			}

			if (customRows[1] && !rows[2].value.toLowerCase().includes(queryString)) {
				rows[2] = {
					key: customRows[1].name,
					value: addCommasToMultiSelectFieldValues(this.renderValue(item, customRows[1].refId), customRows[1]),
				};
			} else {
				// if customRows.length === 1, rows[1] and rows[2] can be the same field's data
				if (rows[1].key === rows[2].key && rows[1].value === rows[2].value) {
					rows[2].value = "";
				}

				rows[2].key = "";
			}
		} else {
			rows[1].key = "";
			rows[2].key = "";
		}

		return rows;
	}

	public sortItems<T extends IModel>(items: T[], view: View) {
		let sortedItems: T[] = [...items];
		const columnFields = view.getAllColumns(items[0]?.ownFeature).map((c) => c.field);
		const sorts = (view?.sorts || []).filter((s) => columnFields.includes(s.column));

		for (let i = sorts.length - 1; i >= 0; i--) {
			const sort = sorts[i];

			if (sort.column) {
				const idColumns = AppFieldActions.isRefId(sort.column);

				if (idColumns) {
					// Handle id columns by removing prefix
					// id column: "f13" -> 13
					sortedItems = sortedItems.slice().sort((row1, row2) => {
						const value1 = StringUtils.refId2Number(this.renderValue(row1, sort.column));
						const value2 = StringUtils.refId2Number(this.renderValue(row2, sort.column));

						return sort.direction * compareTableValues(value1, value2);
					});
				} else {
					sortedItems = sortedItems.slice().sort((row1, row2) => {
						const [value1, value2] = this.getSortValue(row1, row2, sort.column);

						return sort.direction * compareTableValues(value1, value2);
					});
				}
			}
		}
		return sortedItems;
	}

	public getSortsForFeature = (view: View, feature: XyiconFeature): IViewSort[] => {
		if (view) {
			const sorts = view.sorts;
			let columns: IViewColumn[] = [];
			const allColumns = view.getColumnsToCompareWithoutWidth();

			if (sorts && feature) {
				if (view.itemFeature === XyiconFeature.SpaceEditor) {
					columns = allColumns[feature as XyiconFeature.Xyicon | XyiconFeature.Boundary] as IViewColumn[];
				} else {
					columns = allColumns as IViewColumn[];
				}

				return sorts.filter((s) => {
					const field = this.getFieldByRefId(s.column);

					return columns?.find((col) => col?.field === field?.refId);
				});
			}
		}
		return [];
	};

	private isFieldMappedToType(fieldId: string, typeId: string, feature: XyiconFeature) {
		const appState = this._appState;

		const mapping = appState.typeFieldMapping[feature];
		const mappingForType = mapping[typeId];

		if (mappingForType) {
			return mappingForType.has(fieldId);
		}
		return false;
	}

	private _typesAndFieldsCache: {
		[key: string]: {
			[hash: string]: {id: string; label: string; value: boolean}[];
		};
	} = {};

	public getFieldsForType(typeId: string, feature: XyiconFeature, forceRefresh = false) {
		if (!typeId) {
			return [];
		}

		const type = this.getTypeById(typeId);

		if (!type) {
			console.warn(`Type with id ${typeId} doesn't exist. Please report this to the developers.`);
			return [];
		}

		const appState = this._appState;
		const mapping = appState.typeFieldMapping[feature];
		const mappingForType = mapping[typeId];
		const lookupLinkFieldsIds = type.settings?.xyiconLookupLinkFieldList || [];
		const featureFields = appState.fields[feature];

		// The key is used to decide whether we already have a cached version
		const key = `${typeId}_${feature}`;
		// The hash is used to make sure we're recalculating the array if the data changes
		const hash = `_${mappingForType?.size ?? 0}_${lookupLinkFieldsIds.length}_${featureFields.length}_${appState.lists[feature].getVersion()}`;

		if (!this._typesAndFieldsCache[key] || forceRefresh || Object.keys(this._typesAndFieldsCache[key])[0] !== hash) {
			this._typesAndFieldsCache[key] = {};

			const fields = featureFields
				.filter((field) => !lookupLinkFieldsIds.includes(field.id))
				.map((field) => {
					const value = mappingForType ? mappingForType.has(field.id) : false;

					return {
						id: field.id,
						label: field.name,
						value: value,
					};
				});

			// Lookup fields
			const lookupFields = featureFields
				.filter((field) => lookupLinkFieldsIds.includes(field.id) && fields.every((f) => f.id !== field.id))
				.map((field) => ({
					id: field.id,
					label: field.name,
					value: true,
				}));

			fields.push(...lookupFields);
			// End of lookup fields

			this._typesAndFieldsCache[key][hash] = fields.toSorted((fieldA, fieldB) => StringUtils.sortIgnoreCase(fieldA.label, fieldB.label));
		}

		return this._typesAndFieldsCache[key][hash];
	}

	public getFieldsByFeature(sourceFeature: XyiconFeature, inherited = false): IFieldAdapter[] {
		const result: IFieldAdapter[] = [];
		const features = inherited ? this.getInheritedFeatures(sourceFeature) : [sourceFeature];

		for (const feature of features) {
			const defaultFields = (this._appState.defaultFields[feature] || []).filter((field) => field.feature === sourceFeature || field.displayOnLinks);

			result.push(...defaultFields);
			result.push(...(this._appState.fields[feature] || []).filter((field) => sourceFeature === feature || field.displayOnLinks));
		}
		return result;
	}

	public getFieldByName(sourceFeature: XyiconFeature, fieldName: string, inherited: boolean = false): IFieldAdapter {
		return this.getFieldsByFeature(sourceFeature, inherited).find((f) => f.name === fieldName && f.feature === sourceFeature);
	}

	public getInheritedFeatures(feature: XyiconFeature) {
		return inheritedFeatures[feature] || [feature];
	}

	// Returns the features for a given feature that may need to be refreshed
	// as the param feature is using data from them.
	// Used in ModuleView.refreshList
	public getLoadingDependencies(feature: XyiconFeature): XyiconFeature[] {
		return getLoadingDependencyFeatures()[feature] ?? [];
	}

	public getFieldsByFeatures(sourceFeatures: XyiconFeature[], inherited = false): IFieldAdapter[] {
		const result: IFieldAdapter[] = [];

		for (const feature of sourceFeatures) {
			result.push(...this.getFieldsByFeature(feature, inherited));
		}

		return result;
	}

	public getTypesForField(field: FieldModel, typesFeature: XyiconFeature) {
		const appState = this._appState;
		//const typesFeature = typesFeatures[field.feature] || field.feature;

		const featureTypes = appState.types[typesFeature];
		const mapping = appState.typeFieldMapping[field.feature];

		const types = featureTypes.map((type) => {
			const mappingForType = mapping[type.id];
			const value = mappingForType ? mappingForType.has(field.id) : false;

			return {
				id: type.id,
				label: type.name,
				value: value,
			};
		});

		return types.toSorted((typeA, typeB) => StringUtils.sortIgnoreCase(typeA.label, typeB.label));
	}

	public getTypeById(id: string): Type | null {
		return this._appState.typesById[id] || null;
	}

	public getTypeName(typeId: string): string {
		return this.getTypeById(typeId)?.name || "";
	}

	public getViewById(id: string) {
		return this._appState.viewsMap[id] || null;
	}

	public getReportViewById(id: string) {
		const reportViews = this._appState.reportViews ?? [];
		return reportViews.find((view) => view.id === id) || null;
	}

	public getViewOrReportViewById(id: string) {
		return this.getViewById(id) || this.getReportViewById(id);
	}

	// Cached search

	private readonly _modelCache = new Map<IModel, string[]>();
	private _modelCacheVersion = -1;
	private _modelCacheSchemaVersion = "";

	private getModelVersions() {
		let result = 0;

		[
			XyiconFeature.Portfolio,
			XyiconFeature.XyiconCatalog,
			XyiconFeature.Xyicon,
			XyiconFeature.Space,
			XyiconFeature.Boundary,
			XyiconFeature.Markup,
			XyiconFeature.Link,
			XyiconFeature.User,
			XyiconFeature.SpaceVersion,
		].forEach((version) => {
			result += this._appState.lists[version].getVersion();
		});

		return result;
	}

	private getSearchSchemaVersion() {
		return [
			XyiconFeature.Portfolio,
			XyiconFeature.XyiconCatalog,
			XyiconFeature.Xyicon,
			XyiconFeature.Space,
			XyiconFeature.Boundary,
			XyiconFeature.Markup,
		]
			.map((feature) => {
				const fields = [...(this._appState.defaultFields[feature] || []), ...(this._appState.fields[feature] || [])]
					.map((field) => `${field.refId}:${field.dataType}:${field.displayOnLinks}`)
					.join(",");
				const mapping = this._appState.typeFieldMapping[feature] || {};
				const mappingVersion = Object.keys(mapping)
					.sort()
					.map(
						(typeId) =>
							`${typeId}:${Array.from(mapping[typeId] || [])
								.sort()
								.join(",")}`,
					)
					.join("|");

				return `${feature}[${fields}][${mappingVersion}]`;
			})
			.join(";");
	}

	public areModelsCached() {
		return this._modelCacheVersion === this.getModelVersions() && this._modelCacheSchemaVersion === this.getSearchSchemaVersion();
	}

	public searchModelsCached<T extends IModel>(models: T[], findString: string, feature: XyiconFeature, limit = Infinity): T[] {
		if (!findString) {
			return models;
		}

		const modelVersions = this.getModelVersions();
		const searchSchemaVersion = this.getSearchSchemaVersion();

		if (modelVersions > this._modelCacheVersion || searchSchemaVersion !== this._modelCacheSchemaVersion) {
			// cache is outdated -> clear it
			this._modelCache.clear();
		}
		this._modelCacheVersion = modelVersions;
		this._modelCacheSchemaVersion = searchSchemaVersion;

		// const t = window.performance.now();

		const result: T[] = [];

		findString = findString.toLowerCase();
		const fields = this.getFieldsByFeature(feature, true).filter((field) => !isIconField(field));

		// eslint-disable-next-line sonarjs/no-labels
		outer: for (let i = 0; i < models.length; ++i) {
			const model = models[i];

			const cache = this._modelCache.get(model);

			if (cache) {
				if (cache.some((value) => value.includes(findString))) {
					result.push(model);
					if (result.length >= limit) {
						break outer;
					}
				}
			} else {
				const cache = [] as string[];

				this._modelCache.set(model, cache);

				let modelAdded = false;

				for (const field of fields) {
					// TODO skip if model.fieldData doesn't contain anything
					const values = this.getSearchableFieldValues(model, field);
					const lowerCaseValues = values.map((value) => value.toLowerCase());

					cache.push(...lowerCaseValues);

					if (lowerCaseValues.some((value) => value.includes(findString))) {
						if (!modelAdded) {
							result.push(model);
						}

						if (result.length >= limit) {
							break outer;
						}
						modelAdded = true;
						//continue outer;
					}
				}
			}
		}

		// console.log("search", window.performance.now() - t);

		return result;
	}

	public searchModels<T extends IModel>(models: T[], findString: string, feature: XyiconFeature, limit = Infinity): T[] {
		if (!findString) {
			return models;
		}

		const result: T[] = [];

		findString = findString.toLowerCase();
		const fields = this.getFieldsByFeature(feature, true);

		outer: for (let i = 0; i < models.length; ++i) {
			const model = models[i];

			for (const field of fields) {
				// TODO skip if model.fieldData doesn't contain anything
				const lowerCaseValues = this.getSearchableFieldValues(model, field).map((value) => value.toLowerCase());

				if (lowerCaseValues.some((value) => value.includes(findString))) {
					result.push(model);

					if (result.length >= limit) {
						break outer;
					}
					continue outer;
				}
			}
		}

		return result;
	}

	private getSearchableFieldValues(model: IModel, field: IFieldAdapter): string[] {
		if (peopleFieldDataTypes.includes(field.dataType)) {
			return this.getPeopleFieldSearchValues(model, field);
		}

		return this.renderValues(model, field.refId)
			.flat(2)
			.filter((value): value is string => typeof value === "string" && !!value);
	}

	private getPeopleFieldSearchValues(model: IModel, field: IFieldAdapter): string[] {
		const renderUserValue = FieldDataTypes.map[FieldDataType.PeopleSingleChoiceList]?.formatter;
		const values = this.getValueForFormatting(model, field).flatMap((value) => (Array.isArray(value) ? value : [value]));
		const searchValues = values.flatMap((userOrValue) => {
			if (!userOrValue) {
				return [];
			}

			const user = typeof userOrValue === "string" ? this.findUser(userOrValue) : (userOrValue as User | ExternalUser);

			if (!user) {
				return typeof userOrValue === "string" ? [userOrValue] : [];
			}

			const renderedUser = renderUserValue?.(user) ?? "";
			const fullName = user.fullName ?? "";
			const email = user.email ?? "";
			const nameAndEmail = [fullName, email].filter((value) => !!value).join(" ");

			return [renderedUser, fullName, email, nameAndEmail];
		});

		return ArrayUtils.removeDuplicates(searchValues.map((value) => value.trim()).filter((value) => !!value));
	}

	public isFieldHiddenByMasking(item: IModel, field: IFieldAdapter) {
		if (field) {
			if (!field.isAssignedByModel) {
				return false;
			}

			// catalog only exists if item is Xyicon or Catalog
			const catalog = this.getCatalogById((item as Xyicon).catalogId || item.id);
			const isVisible =
				field.feature === XyiconFeature.Xyicon
					? catalog?.xyiconVisibleFields.includes(field.refId)
					: catalog?.catalogVisibleFields.includes(field.refId);

			// hidden: xyicon/catalog + assigned by model + selectSlider is unassigned
			return catalog && field.isAssignedByModel && !isVisible;
		}
		return true;
	}

	public searchModelCustomFields(model: IModel, findString: string, feature: XyiconFeature): IFieldAdapter[] {
		const result: IFieldAdapter[] = [];

		if (!findString) {
			return result;
		}

		const fields = this.getFieldsByFeature(feature, true).filter(
			(f) => isCardLayoutSupportedForField(f) || peopleFieldDataTypes.includes(f.dataType),
		);
		const lowerCaseFindString = findString.toLowerCase();

		for (const field of fields) {
			const isFieldHidden = this.isFieldHiddenByMasking(model, field);

			if (isFieldHidden) {
				continue;
			}

			const isMatchingField = this.getSearchableFieldValues(model, field).some((value) => value.toLowerCase().includes(lowerCaseFindString));

			if (isMatchingField) {
				result.push(field);
			}
		}

		return result;
	}

	public renderInitials(userId: string) {
		const user = this.findUser(userId);

		if (user) {
			return FormatUtils.formatInitials(user);
		}
		return "";
	}

	public findUser(userId: string): User {
		const user = this.getFeatureItemById<User>(userId, XyiconFeature.User);

		if (!user) {
			return this.getFeatureItemById<ExternalUser>(userId, XyiconFeature.ExternalUser);
		}

		return user;
	}

	public findUserGroup(userGroupId: string): UserGroup {
		return this.getFeatureItemById<UserGroup>(userGroupId, XyiconFeature.UserGroup);
	}

	public renderName(userId: string) {
		const user = this.findUser(userId);

		if (user) {
			return user.fullName;
		}

		const removedUsersList = this._appState.removedUsersList ?? [];
		const removedUser = removedUsersList.find((user) => user?.userID === userId);

		if (removedUser) {
			return `${removedUser.firstName} ${removedUser.lastName}`;
		}

		return "";
	}

	//TODO: separate this into two functions for Views and Reports.
	public isNameValidForViewOrReport(value: string, feature: XyiconFeature, viewOrReportId?: string, isReport?: boolean): boolean {
		if (!value.trim()) {
			return false;
		}

		const viewsOrReports = isReport ? this.getReportViews() : this.getViews(feature);

		return viewsOrReports.every((item) => item.id === viewOrReportId || !StringUtils.equalsIgnoreCase(item.name, value));
	}

	public isPortfolioNameValid(name: string, id?: string) {
		if (!name.trim()) {
			return false;
		}

		const portfolios = this.getList(XyiconFeature.Portfolio);

		return !portfolios.some((p) => p && p.id !== id && StringUtils.equalsIgnoreCase(p.name, name));
	}

	public isVersionSetNameValid(name: string, id: string) {
		if (!name.trim()) {
			return false;
		}

		const versions = this.getList(XyiconFeature.SpaceVersion);

		return !versions.some((v) => v && v.id !== id && StringUtils.equalsIgnoreCase(v.name, name));
	}

	public isModelValidForCatalog(value: string, catalog: Catalog) {
		if (!value) {
			return false;
		}

		return this.getList<Catalog>(XyiconFeature.XyiconCatalog).every(
			(c: Catalog) => c.id === catalog?.id || !StringUtils.equalsIgnoreCase(c.model, value),
		);
	}

	public getNumberInputErrorMessage = (value: string, dataTypeSettings: INumericFieldSettingsDefinition, onlyNaturalNumbers: boolean): string => {
		if (!value) {
			// We allow empty inputs for numeric field values, so: value === "" || value === null || value === undefined
			return "";
		}

		const formatting = dataTypeSettings?.formatting ?? "general";

		// Base numeric pattern (plain input)
		const plainNumeric = onlyNaturalNumbers ? "\\d+" : "-?(?:\\d+\\.?\\d*|\\.\\d+)";

		// Map of format-specific patterns
		const formatPatterns: Record<string, string> = {
			general: plainNumeric,
			percentage: `(${plainNumeric}|${plainNumeric}%)`,
			"comma-style": `(${plainNumeric}|-?(?:\\d{1,3}(?:,\\d{3})*)(?:\\.\\d+)?)`,
			currency: onlyNaturalNumbers
				? // no decimals, allow optional $, optional grouping
					`-?\\$?(?:\\d+|\\d{1,3}(?:,\\d{3})*)`
				: // allow decimals, optional $, optional grouping
					`-?\\$?(?:\\d+|\\d{1,3}(?:,\\d{3})*)(?:\\.\\d+)?`,
		};

		// Build final regex
		const pattern = `^${formatPatterns[formatting]}$`;
		const regex = new RegExp(pattern);

		return regex.test(value) ? "" : "Enter a Numeric value!";
	};

	public getIndividualPortfolioPermissionSet(portfolioId: string): PermissionSet | null {
		return this.getPortfolioPermissionContext(portfolioId).individualPermissionSet;
	}

	public getPortfolioGroupPermissionSets(portfolioId: string): PermissionSet[] {
		return this.getPortfolioPermissionContext(portfolioId).groupPermissionSets.slice();
	}

	public getPortfolioPermission(portfolioId: string): Permission {
		const user = this._appState.user;

		if (!user) {
			return Permission.None;
		}

		if (user.isAdmin) {
			return Permission.Delete;
		}

		let permission = Permission.None;

		const {individualPermissionSet, groupPermissionSets} = this.getPortfolioPermissionContext(portfolioId);

		if (individualPermissionSet) {
			permission = individualPermissionSet.portfolioPermission;
		} else {
			const groupPermissionSet = groupPermissionSets[0];

			permission = groupPermissionSet?.portfolioPermission;
		}

		return permission;
	}

	public getPortfolioFieldPermissionForPortfolio(portfolioId: string, fieldRefId: string): Permission {
		const user = this._appState.user;

		if (!user || !portfolioId) {
			return Permission.None;
		}

		if (user.isAdmin) {
			return Permission.Delete;
		}

		const {individualPermissionSet, groupPermissionSets} = this.getPortfolioPermissionContext(portfolioId);

		if (fieldRefId.includes("name")) {
			if (individualPermissionSet) {
				return individualPermissionSet.portfolioPermission;
			}

			const groupPermissionSet = groupPermissionSets[0];

			if (groupPermissionSet) {
				return groupPermissionSet.portfolioPermission;
			}
		}

		if (individualPermissionSet) {
			return Math.min(individualPermissionSet.portfolioPermission, individualPermissionSet.getPortfolioFieldPermission(fieldRefId));
		}

		const groupPermissionSet = groupPermissionSets[0];

		if (groupPermissionSet) {
			return Math.min(groupPermissionSet.portfolioPermission, groupPermissionSet.getPortfolioFieldPermission(fieldRefId));
		}

		return Permission.None;
	}

	public getModuleTypePermission(typeId: string, feature: XyiconFeature, portfolioId: string = this._appState.activePortfolioId) {
		if (!this._appState.user || !typeId) {
			return Permission.None;
		}

		if (this._appState.user.isAdmin) {
			return Permission.Delete;
		}

		let typePermission: Permission = Permission.None;

		const {individualPermissionSet, groupPermissionSets} = this.getPortfolioPermissionContext(portfolioId);

		if (individualPermissionSet) {
			const featurePermission = individualPermissionSet.featurePermissions.find((fP) => fP.feature === feature);

			typePermission = individualPermissionSet.getObjectPermission(typeId, feature);
			if (featurePermission && !featurePermission.canAccess) {
				typePermission = typePermission > Permission.None ? Permission.View : Permission.None;
			}
		} else {
			const groupPermissionSet = groupPermissionSets[0];

			if (groupPermissionSet) {
				const featurePermission = groupPermissionSet.featurePermissions.find((fP) => fP.feature === feature);
				const permission = groupPermissionSet.getObjectPermission(typeId, feature);

				if (featurePermission && !featurePermission.canAccess) {
					typePermission = permission > Permission.None ? Permission.View : Permission.None;
				}
				typePermission = permission;
			}
		}

		return typePermission;
	}

	// checks all Types in a Feature, if any of them has the given permission, returns true
	public hasAnyTypeTheGivenPermissionInModule(feature: XyiconFeature, permission: Permission) {
		if (!this._appState.user) {
			return false;
		}

		if (this._appState.user.isAdmin) {
			return true;
		}

		const portfolioId = this._appState.activePortfolioId;

		let hasPermission: boolean;

		const {individualPermissionSet, groupPermissionSets} = this.getPortfolioPermissionContext(portfolioId);

		if (individualPermissionSet) {
			const featurePermission = individualPermissionSet.featurePermissions.find((fP) => fP.feature === feature);

			hasPermission = featurePermission?.objectPermissions.some((oP) => oP.permission >= permission);

			if (featurePermission && !featurePermission.canAccess) {
				hasPermission = permission === Permission.View;
			}
		} else {
			const groupPermissionSet = groupPermissionSets[0];

			if (groupPermissionSet) {
				const featurePermission = groupPermissionSet.featurePermissions.find((fP) => fP.feature === feature);

				hasPermission = featurePermission?.objectPermissions.some((oP) => oP.permission >= permission);

				if (featurePermission && !featurePermission.canAccess) {
					hasPermission = permission === Permission.View;
				}
			}
		}

		return hasPermission;
	}

	private getSpaceItemHaveGivenPermission(spaceItem: SpaceItem, permission: Permission) {
		if (["xyicon", "boundary"].includes(spaceItem.spaceItemType)) {
			const item = spaceItem.modelData;

			return this.getModuleTypePermission(item.typeId, item.ownFeature) >= permission;
		} else {
			return true;
		}
	}

	public someSpaceItemsHaveGivenPermission(items: SpaceItem[], permission: Permission): boolean {
		return items.some((spaceItem: SpaceItem) => this.getSpaceItemHaveGivenPermission(spaceItem, permission));
	}

	public filterSpaceItemsForPermission(items: SpaceItem[], permission: Permission): SpaceItem[] {
		return items.filter((spaceItem: SpaceItem) => this.getSpaceItemHaveGivenPermission(spaceItem, permission));
	}

	public getNumberOfSpaceItemsWithoutPermission = (items: SpaceItem[], permission: Permission) => {
		let xyiconsWithNoPermissionCount = 0;

		items.forEach((spaceItem: SpaceItem) => {
			if (["xyicon", "boundary"].includes(spaceItem?.spaceItemType)) {
				const item = spaceItem.modelData;

				if (item && this.getModuleTypePermission(item.typeId, item.ownFeature) < permission) {
					xyiconsWithNoPermissionCount++;
				}
			}
		});

		return xyiconsWithNoPermissionCount;
	};

	/**
	 * Returns the field's permission on the added item (by type). If more items were added, the highest permission will be returned.
	 * Individual portfolio permission gets priority over groups. If more group permissions are existing, the highest permission will be returned.
	 * Includes the admin right check (returns Delete if the user is admin). Checks if the user is undefined or not.
	 *
	 * @param   field  IFieldAdapter type (not only the refId, but the Field).
	 * @param   items  [item] or items as array.
	 * @returns Returns a Permission (None, View, Update, Delete)
	 */
	public getFieldPermission(field: IFieldAdapter, items?: IModel[]): Permission {
		if (!this._appState.user) {
			return Permission.None;
		}

		if (this._appState.user.isAdmin) {
			return Permission.Delete;
		}

		const {feature, refId} = field;

		const portfolioID = this._appState.activePortfolioId;
		const permissionContext = this.getPortfolioPermissionContext(portfolioID);
		const groupPermissionSet = permissionContext.groupPermissionSets[0];
		const individualPermissionSet = permissionContext.individualPermissionSet;

		let permission = Permission.None;

		if (feature === XyiconFeature.Portfolio) {
			if (items?.length > 0) {
				const allPortfolioPermission = items.map((portfolio) => this.getPortfolioFieldPermissionForPortfolio(portfolio.id, refId));

				permission = Math.max(...allPortfolioPermission);
			} else {
				if (individualPermissionSet) {
					permission = individualPermissionSet.getPortfolioFieldPermission(refId);
				} else {
					if (groupPermissionSet) {
						permission = groupPermissionSet.getPortfolioFieldPermission(refId);
					}
				}
			}

			// Note that GeneralPermissions override field permissions
			// https://web.microsoftstream.com/video/0461a4a5-586f-48e0-9011-2ed2a91b4243?st=1288
		} else if (feature === XyiconFeature.XyiconCatalog) {
			return this._appState.user.getOrganizationPermission(XyiconFeature.XyiconCatalog);
		} else {
			if (items?.length > 0) {
				const allItemPermission = items.map((item) => {
					let itemPermission = Permission.None;
					const itemPortfolioId = item.portfolioId ?? portfolioID;
					const itemPermissionContext = this.getPortfolioPermissionContext(itemPortfolioId);
					const itemGroupPermissionSet = itemPermissionContext.groupPermissionSets[0];
					const itemIndividualPermissionSet = itemPermissionContext.individualPermissionSet;

					// when an item gets inherited DOL field from another module
					// we should display it disabled
					if (field.displayOnLinks && field.feature !== item.ownFeature) {
						return Permission.View;
					}

					if (itemIndividualPermissionSet) {
						const typePermission = itemIndividualPermissionSet.getObjectPermission(item.typeId, feature);
						const fieldPermission = itemIndividualPermissionSet.getFieldPermission(refId, feature);

						itemPermission = Math.min(typePermission, fieldPermission);
					} else {
						if (itemGroupPermissionSet) {
							const typePermission = itemGroupPermissionSet.getObjectPermission(item.typeId, feature);
							const fieldPermission = itemGroupPermissionSet.getFieldPermission(refId, feature);

							itemPermission = Math.min(typePermission, fieldPermission);
						}
					}

					return itemPermission;
				});

				permission = Math.max(...allItemPermission);
			} else {
				if (individualPermissionSet) {
					permission = individualPermissionSet.getFieldPermission(refId, feature);
				} else {
					if (groupPermissionSet) {
						permission = groupPermissionSet.getFieldPermission(refId, feature);
					}
				}
			}
		}

		return permission;
	}

	private getPortfolioPermissionContext(portfolioId: string): IPortfolioPermissionContext {
		if (!portfolioId) {
			return {
				individualPermissionSet: null,
				groupPermissionSets: [],
			};
		}

		const cache = this.getPortfolioPermissionResolutionCache();

		return {
			individualPermissionSet: cache.individualPermissionSetByPortfolioId[portfolioId] ?? null,
			groupPermissionSets: cache.groupPermissionSetsByPortfolioId[portfolioId] || [],
		};
	}

	private getPortfolioPermissionResolutionCache(): IPortfolioPermissionResolutionCache {
		const user = this._appState.user;
		const userData = user?.data ?? null;
		const portfolioPermissionListRef = user?.portfolioPermissionList ?? null;
		const portfolioGroupPermissionListRef = user?.portfolioGroupPermissionList ?? null;
		const userVersion = this._appState.lists[XyiconFeature.User]?.getVersion() ?? -1;
		const permissionSetVersion = this._appState.lists[XyiconFeature.PermissionSet]?.getVersion() ?? -1;
		const portfolioGroupVersion = this._appState.lists[XyiconFeature.PortfolioGroup]?.getVersion() ?? -1;

		const cache = this._portfolioPermissionResolutionCache;

		if (
			cache &&
			cache.user === user &&
			cache.userData === userData &&
			cache.portfolioPermissionListRef === portfolioPermissionListRef &&
			cache.portfolioGroupPermissionListRef === portfolioGroupPermissionListRef &&
			cache.userVersion === userVersion &&
			cache.permissionSetVersion === permissionSetVersion &&
			cache.portfolioGroupVersion === portfolioGroupVersion
		) {
			return cache;
		}

		const nextCache: IPortfolioPermissionResolutionCache = {
			user,
			userData,
			portfolioPermissionListRef,
			portfolioGroupPermissionListRef,
			userVersion,
			permissionSetVersion,
			portfolioGroupVersion,
			individualPermissionSetByPortfolioId: {},
			groupPermissionSetsByPortfolioId: {},
		};

		if (user) {
			for (const portfolioPermission of user.portfolioPermissionList) {
				const portfolioId = portfolioPermission.portfolioID;

				if (!portfolioId || portfolioId in nextCache.individualPermissionSetByPortfolioId) {
					continue;
				}

				nextCache.individualPermissionSetByPortfolioId[portfolioId] =
					this.getFeatureItemById<PermissionSet>(portfolioPermission.portfolioPermissionSetID, XyiconFeature.PermissionSet) ?? null;
			}

			for (const portfolioGroupPermission of user.portfolioGroupPermissionList) {
				const permissionSet = this.getFeatureItemById<PermissionSet>(portfolioGroupPermission.portfolioPermissionSetID, XyiconFeature.PermissionSet);
				const group = this.getFeatureItemById<PortfolioGroup>(portfolioGroupPermission.portfolioGroupID, XyiconFeature.PortfolioGroup);

				if (!permissionSet || !group) {
					continue;
				}

				for (const portfolioId of group.portfolioIds) {
					if (!portfolioId) {
						continue;
					}

					nextCache.groupPermissionSetsByPortfolioId[portfolioId] = nextCache.groupPermissionSetsByPortfolioId[portfolioId] || [];
					nextCache.groupPermissionSetsByPortfolioId[portfolioId].push(permissionSet);
				}
			}
		}

		this._portfolioPermissionResolutionCache = nextCache;

		return nextCache;
	}

	public async deleteSpaceItemsFromActiveSpace(spaceItemIdsToDelete: string[], feature: XyiconFeature, addFullscreenLoaderText: boolean = true) {
		const spaceViewRenderer = this._appState.app.spaceViewRenderer;

		if (this._appState.space?.id) {
			const itemManager = spaceViewRenderer.getItemManager(feature);

			let linksNeedUpdate = false;
			const deletedModels: IModel[] = [];
			const deletedSpaceItems: SpaceItem[] = [];
			const unselectedItems: SpaceItem[] = [];

			for (const spaceItemId of spaceItemIdsToDelete) {
				const spaceItem = itemManager.getItemById(spaceItemId);

				if (spaceItem) {
					if (spaceItem.spaceItemType === "xyicon") {
						if ((spaceItem as Xyicon3D).linkInstanceId > -1) {
							linksNeedUpdate = true;
						}
					} else if (spaceItem.spaceItemType === "boundary") {
					}

					deletedSpaceItems.push(spaceItem);
					deletedModels.push(spaceItem.modelData);
					if (spaceItem.isSelected) {
						spaceItem.deselect();
						unselectedItems.push(spaceItem);
					}
				}
			}

			if ([XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(feature)) {
				(itemManager as XyiconManager | BoundaryManager).captionManager.hideTextGroup(
					(deletedSpaceItems as CaptionedItem[]).filter(CaptionManager.filterVisibleCaptionedItems).map((item) => item.caption),
					true,
				);
			}

			for (const spaceItem of deletedSpaceItems) {
				spaceItem.destroy(false);
			}

			if (unselectedItems.length > 0) {
				spaceViewRenderer.spaceItemController.updateActionBar(false, true);
			}

			if (deletedModels.length > 0) {
				itemManager.signals.itemsRemove.dispatch(deletedModels);
			}

			if (linksNeedUpdate) {
				spaceViewRenderer.spaceItemController.linkIconManager.update();
			}

			itemManager.updateSelectionBox();
			spaceViewRenderer.spaceItemController.updateBoundingBox();
			await spaceViewRenderer.spaceItemController.updateFilterState(undefined, false, undefined, addFullscreenLoaderText);

			const captionAffectedItems: CaptionedItem[] = deletedSpaceItems.filter(
				(spaceItem) => spaceItem.spaceItemType === "xyicon" || spaceItem.spaceItemType === "boundary",
			) as CaptionedItem[];

			return this.updateSpaceEditorCaptionsForAffectedAndLinkedItems(feature, captionAffectedItems);
		}
	}

	public async mergeBoundaries(parentBoundary: Boundary, childBoundaries: Boundary[], showNotification: boolean = true) {
		if (!parentBoundary || childBoundaries?.length < 1) {
			console.warn("Attempted to merge invalid boundary data. This shouldn't have happened...");
			return;
		}

		const boundarySpaceMapsToMerge: BoundarySpaceMap[] = [];
		let firstChildBoundaryRefId: string = null;

		for (const childBoundary of childBoundaries) {
			const childBoundarySpaceMaps = [...childBoundary.boundarySpaceMaps];

			boundarySpaceMapsToMerge.push(...childBoundarySpaceMaps);

			for (const boundarySpaceMap of childBoundarySpaceMaps) {
				if (!firstChildBoundaryRefId) {
					firstChildBoundaryRefId = boundarySpaceMap.refId;
				}
			}
		}

		const params: MergeBoundaryRequest = {
			childBoundarySpaceMapIDList: boundarySpaceMapsToMerge.map((boundarySpaceMap: BoundarySpaceMap) => boundarySpaceMap.id),
			parentBoundaryID: parentBoundary.id,
			portfolioID: this._appState.activePortfolioId,
		};

		const {result, error} = await this._appState.app.transport.requestForOrganization<MergedBoundaryDto>({
			url: "boundaries/merge",
			method: XHRLoader.METHOD_POST,
			params,
		});

		const boundaryToBeUpdated = this.getFeatureItemById<Boundary>(result.parentBoundaryID, XyiconFeature.Boundary);
		boundaryToBeUpdated.updateLastModifiedData(result);

		if (error) {
			notify(this._appState.app.notificationContainer, {
				title: "Error",
				type: NotificationType.Error,
				description: "Boundaries couldn't be merged. Please refresh your browser, and try again.",
			});
		} else {
			// Re-add merged boundaries, so they're refreshed correctly
			// (boundary type, height, color, etc is much easier and safer to update this way)
			// this.onBoundariesMerged(result);
			// SignalR is also called for the entity that has triggered it, so we shouldn't call "onBoundariesMerged" again!
		}

		const isMultipleChild = boundarySpaceMapsToMerge.length > 1;

		if (showNotification) {
			notify(this._appState.app.notificationContainer, {
				title: "Boundaries merged successfully",
				description: `${isMultipleChild ? `${boundarySpaceMapsToMerge.length} boundaries were` : `${firstChildBoundaryRefId} was`} merged to ${parentBoundary.refId}`,
				type: NotificationType.Success,
			});
		}
	}

	public isTypeNameValid(name: string, type: Type) {
		if (!name.trim()) {
			return false;
		}

		const otherFieldsWithSameName = this._appState.types[type.feature].find((t) => t.id !== type.id && StringUtils.equalsIgnoreCase(t.name, name));

		return !otherFieldsWithSameName;
	}

	public isFieldNameValid(name: string, field: FieldModel) {
		if (!name.trim()) {
			return false;
		}

		const otherFieldsWithSameName = this._appState.fields[field.feature].find((f) => f.id !== field.id && StringUtils.equalsIgnoreCase(f.name, name));

		return !otherFieldsWithSameName;
	}

	public isSpaceNameValid(name: string, space: Space) {
		if (!name.trim()) {
			return false;
		}

		const spaces = this._appState.lists[XyiconFeature.Space].array;

		return !spaces.some((s) => s && s.id !== space.id && StringUtils.equalsIgnoreCase(s.name, name));
	}

	public isUserGroupNameValid(name: string, userGroup: UserGroup) {
		if (!name.trim()) {
			return false;
		}

		const userGroups = this._appState.lists[XyiconFeature.UserGroup].array;

		return !userGroups.some((uG) => uG && uG.id !== userGroup.id && StringUtils.equalsIgnoreCase(uG.name, name));
	}

	public isPortfolioGroupNameValid(name: string, portfolioGroup: PortfolioGroup) {
		if (!name.trim()) {
			return false;
		}

		const portfolioGroups = this._appState.lists[XyiconFeature.PortfolioGroup].array;

		return !portfolioGroups.some((pG) => pG && pG.id !== portfolioGroup.id && StringUtils.equalsIgnoreCase(pG.name, name));
	}

	public isPermissionSetNameValid(name: string, id: string) {
		if (!name.trim()) {
			return false;
		}

		const permissionSets = this._appState.lists[XyiconFeature.PermissionSet].array;

		return !permissionSets.some((pS) => pS && pS.id !== id && StringUtils.equalsIgnoreCase(pS.name, name));
	}

	public doesXyiconHaveLinkIcon = (xyicon: Xyicon): boolean => {
		return (
			this.getLinksXyiconXyicon(xyicon.id).filter((l) => !l.link.isEmbedded).length + this.getCrossPortfolioLinksXyiconXyicon(xyicon.id).length > 0
		);
	};

	// Adding embedded xyicons and boundaryspacemaps to the result
	public getNumberOfModels(items: IModel[]) {
		let numberOfAffectedItems = 0;

		for (const item of items) {
			if (item.ownFeature === XyiconFeature.Xyicon) {
				const embeddedXyicons = (item as Xyicon).embeddedXyicons;
				const embeddedXyiconsThatAreNotSelected = embeddedXyicons.filter((x) => !items.includes(x as IModel));

				numberOfAffectedItems += 1 + embeddedXyiconsThatAreNotSelected.length;
			} else if ((item as Boundary).isBoundary) {
				numberOfAffectedItems += (item as Boundary).boundarySpaceMaps.size;
			} else {
				++numberOfAffectedItems;
			}
		}

		return numberOfAffectedItems;
	}

	// Adding embedded xyicons to the result
	public getNumberOfSpaceItems(items: SpaceItem[]) {
		let numberOfEmbeddedXyicons = 0;

		for (const item of items) {
			if (item.spaceItemType === "xyicon") {
				const xyicon = item.modelData as Xyicon;

				numberOfEmbeddedXyicons += xyicon.embeddedXyicons.length;
			}
		}

		return items.length + numberOfEmbeddedXyicons;
	}

	public async deleteSpaceItem(item: IModel) {
		const {xyiconManager} = this._appState.app.spaceViewRenderer;
		const xyicon3D = xyiconManager.getItemById(item.id) as Xyicon3D;

		if (xyicon3D) {
			xyicon3D.destroy(false);
		}

		await this.deleteItems([item], item.ownFeature);

		if (item.ownFeature === XyiconFeature.Xyicon) {
			const parentXyicon = (item as Xyicon).parentXyicon;

			if (parentXyicon) {
				const parentXyicon3D = xyiconManager.getItemById(parentXyicon.id) as Xyicon3D;

				if (parentXyicon3D) {
					parentXyicon3D.updateCounter();
				}
			}
		}
	}

	public getDefaultFormattingRuleSet(feature: XyiconFeature): IFormattingRuleSet {
		const types = this.getTypesByFeature(feature);

		const indicator: {
			[key: string]: string;
		} = {};

		const highlight: {
			[key: string]: string;
		} = {};

		for (const type of types) {
			indicator[type.name] = null;
			highlight[type.name] = null;
		}

		return {
			enabled: true,
			indicator: indicator,
			highlight: highlight,
		};
	}

	public getDefaultLayerSettings(feature: XyiconFeature): ILayerSettings {
		if (feature === XyiconFeature.Markup) {
			const defaultHiddenValue = Constants.DEFAULT_VALUE_FOR_INCLUDED_HIDE;
			const defaultLockedValue = Constants.DEFAULT_VALUE_FOR_INCLUDED_POSITION_LOCK;

			const defaultProps = {
				isHidden: defaultHiddenValue,
				isPositionLocked: defaultLockedValue,
			};

			return {
				included: {
					[MarkupType.Cloud]: {
						title: MarkupType[MarkupType.Cloud],
						...defaultProps,
					},
					[MarkupType.Arrow]: {
						title: MarkupType[MarkupType.Arrow],
						...defaultProps,
					},
					[MarkupType.BidirectionalArrow]: {
						title: MarkupType[MarkupType.BidirectionalArrow],
						...defaultProps,
					},
					[MarkupType.Line]: {
						title: MarkupType[MarkupType.Line],
						...defaultProps,
					},
					[MarkupType.DashedLine]: {
						title: MarkupType[MarkupType.DashedLine],
						...defaultProps,
					},
					[MarkupType.PencilDrawing]: {
						title: MarkupType[MarkupType.PencilDrawing],
						...defaultProps,
					},
					[MarkupType.HighlightDrawing]: {
						title: MarkupType[MarkupType.HighlightDrawing],
						...defaultProps,
					},
					[MarkupType.TextBox]: {
						title: MarkupType[MarkupType.TextBox],
						...defaultProps,
					},
					[MarkupType.Rectangle]: {
						title: MarkupType[MarkupType.Rectangle],
						...defaultProps,
					},
					[MarkupType.Ellipse]: {
						title: MarkupType[MarkupType.Ellipse],
						...defaultProps,
					},
					[MarkupType.Triangle]: {
						title: MarkupType[MarkupType.Triangle],
						...defaultProps,
					},
					[MarkupType.Cross]: {
						title: MarkupType[MarkupType.Cross],
						...defaultProps,
					},
					[MarkupType.LinearDistance]: {
						title: MarkupType[MarkupType.LinearDistance],
						...defaultProps,
					},
					[MarkupType.RectangleArea]: {
						title: MarkupType[MarkupType.RectangleArea],
						...defaultProps,
					},
					[MarkupType.NonlinearDistance]: {
						title: MarkupType[MarkupType.NonlinearDistance],
						...defaultProps,
					},
					[MarkupType.IrregularArea]: {
						title: MarkupType[MarkupType.IrregularArea],
						...defaultProps,
					},
					// [MarkupType.Stamp]: {
					// 	title: MarkupType[MarkupType.Stamp],
					// 	...defaultProps
					// },
					// [MarkupType.Photo]: {
					// 	title: MarkupType[MarkupType.Photo],
					//  ...defaultProps
					// }
					[MarkupType.Callout]: {
						title: MarkupType[MarkupType.Callout],
						...defaultProps,
					},
					[MarkupType.Photo360]: {
						title: MarkupType[MarkupType.Photo360],
						...defaultProps,
					},
				},
			};
		} else {
			const types = this.getTypesByFeature(feature);

			const defaultValueForIncludedLock = Constants.DEFAULT_VALUE_FOR_INCLUDED_POSITION_LOCK;
			const defaultValueForIncludedHide = Constants.DEFAULT_VALUE_FOR_INCLUDED_HIDE;

			const included: ILayerColumn = {};

			for (const type of types) {
				included[type.name] = {
					title: type.name,
					isHidden: defaultValueForIncludedHide,
					isPositionLocked: defaultValueForIncludedLock,
				};
			}

			return {
				included: included,
			};
		}
	}

	public getDefaultCaptionSettings(): ICaptionSettings {
		return {
			checkList: [],
			individualCaptionStyles: {},
			fontColor: {
				hex: "000000",
				transparency: 0,
			},
			backgroundColor: {
				hex: "ECEFF0",
				transparency: 0,
			},
			fontSize: Constants.SIZE.FONT.default,
			fontFamily: "Roboto",
			isBold: true,
			isItalic: false,
			isUnderlined: false,
		};
	}

	public getBooleanFieldCustomLabelSettingsByReportSortDirection(field: IFieldAdapter, direction: ReportSortDirection): string {
		if (field.dataType !== FieldDataType.Boolean) {
			return direction;
		} else {
			const booleanLabels = Object.values(field.dataTypeSettings) as string[];
			const sortedBooleanLabels = booleanLabels.toSorted((a: string, b: string) => StringUtils.sortIgnoreCase(a, b));

			return ReportSortDirection.ASC === direction ? sortedBooleanLabels[0] : sortedBooleanLabels[1];
		}
	}

	public getBooleanFieldCustomLabelSettingsByFilterOperator(field: IFieldAdapter, filterOperator: FilterOperator): string {
		if (field.dataType !== FieldDataType.Boolean) {
			return filterOperator;
		} else {
			const operatorIsTrue = filterOperator === FilterOperator.IS_TRUE;
			const settings = field.dataTypeSettings;

			return operatorIsTrue ? settings.displayLabelForTrue : settings.displayLabelForFalse;
		}
	}
}
